import type { AnyID } from '@types'
import { straightModuleLateralSnapDistance } from '@utils/sceneObjects/items/itemObbUtils'
import { Euler, Matrix4, Quaternion, Vector3 } from 'three'

/** Letra de mano D/I al final del catálogo. */
function unitArmHandednessLetter(catalogID: unknown): 'd' | 'i' | null {
  const normalized = String(catalogID ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
  const match = normalized.match(/\s([di])$/i)
  return match ? (match[1].toLowerCase() as 'd' | 'i') : null
}

export type RinconeroUnitLike = {
  catalogID?: unknown
  width?: number
  thickness?: number
  regleta?: number
  morphs?: Array<{ name?: string }>
  matrix?: { decompose: (p: Vector3, q: Quaternion, s: Vector3) => void }
}

/** Sufijos de acabado (Li, La, …) — no confundir con rinconero izquierdo. */
const ACABADO_SUFFIX_REGEX = /\s+(li|la|ru|r2)(\s+go)?$/i

export type RinconeroHandednessContext = {
  hand?: unknown
  mano?: unknown
}

/** Activar logs de depuración al dibujar rinconero (consola del navegador). */
export const RINCONERO_DRAW_DEBUG = false

/**
 * Logs de snap/colisión rinconero ↔ otras unidades (arrastre + collideItems).
 * Desactivados por defecto. Activar en consola:
 *   window.__enableRinconeroSnapDebug__()
 * o: window.__RINCONERO_SNAP_DEBUG__ = true (sin recargar)
 * Historial siempre disponible: window.__rinconeroSnapHistory()
 */
export function isRinconeroSnapDebug(): boolean {
  return (
    typeof window !== 'undefined' &&
    (window as Window & { __RINCONERO_SNAP_DEBUG__?: boolean }).__RINCONERO_SNAP_DEBUG__ === true
  )
}

/** @deprecated Usar isRinconeroSnapDebug() — el valor al import ya no se usa. */
export const RINCONERO_SNAP_DEBUG = false

export function enableRinconeroSnapDebug(): void {
  if (typeof window === 'undefined') return
  ;(window as Window & { __RINCONERO_SNAP_DEBUG__?: boolean }).__RINCONERO_SNAP_DEBUG__ = true
  console.warn(
    '[RinconeroSnap] Depuración activa. Filtra consola por "RinconeroSnap". Historial: window.__rinconeroSnapHistory()',
  )
}

let rinconeroSnapLogSeq = 0
let lastFaceLogKey = ''

export type RinconeroSnapLogPhase =
  'drag' | 'collide' | 'face' | 'defer' | 'occupancy' | 'hold' | 'approach' | 'diagnosis'

export type RinconeroSnapReasonCode =
  | 'OK_GATE_OPEN_FACE'
  | 'OK_SNAP_LATERAL'
  | 'OK_SNAP_LEG'
  | 'OK_SNAP_APPLIED'
  | 'BLOCK_NO_MATRIX'
  | 'BLOCK_INTERIOR_L'
  | 'BLOCK_CUSHIONED_FACE'
  | 'BLOCK_NO_OPEN_HEMISPHERE'
  | 'BLOCK_DISTANCE'
  | 'BLOCK_OBB_NO_TRIGGER'
  | 'BLOCK_MOVING_AWAY'
  | 'BLOCK_FACE_OCCUPIED'
  | 'BLOCK_OVERLAP'
  | 'BLOCK_DEFER_ROW'
  | 'BLOCK_PENETRATION'
  | 'BLOCK_COLLIDE_NO_FACE'
  | 'BLOCK_SNAP_POSITION'

export type RinconeroSnapDiagnosisPhase =
  'drag-gate' | 'drag-attempt' | 'drag-penetration' | 'collide' | 'release'

export type RinconeroSnapDiagnosis = {
  flow: 'module-to-rinconero'
  phase: RinconeroSnapDiagnosisPhase
  verdict: 'allow' | 'block' | 'snap'
  reason: RinconeroSnapReasonCode
  hint: string
  moduleId?: string
  rinconeroId?: string
  moduleCatalog?: unknown
  rinconeroCatalog?: unknown
  metrics: Record<string, unknown>
}

const RINCONERO_SNAP_HINTS: Record<RinconeroSnapReasonCode, string> = {
  OK_GATE_OPEN_FACE: 'Cara abierta válida; puede intentar collideItems.',
  OK_SNAP_LATERAL: 'Snap lateral (continuar fila) aplicado.',
  OK_SNAP_LEG: 'Snap en pata de la L aplicado.',
  OK_SNAP_APPLIED: 'Posición guardada en store tras snap.',
  BLOCK_NO_MATRIX: 'El rinconero no tiene matrix en store.',
  BLOCK_INTERIOR_L: 'Acercamiento por esquina interior de la L.',
  BLOCK_CUSHIONED_FACE:
    'Acercamiento por lado con respaldo. Rota el módulo: lado abierto hacia el rinconero.',
  BLOCK_NO_OPEN_HEMISPHERE: 'No estás en hemisferio lateral abierto ni pata.',
  BLOCK_DISTANCE: 'Demasiado lejos del punto objetivo de snap.',
  BLOCK_OBB_NO_TRIGGER: 'Cerca pero sin intersección OBB ni umbral de imán.',
  BLOCK_MOVING_AWAY: 'Arrastre alejándose del punto de snap.',
  BLOCK_FACE_OCCUPIED: 'Esa cara abierta del rinconero ya está ocupada.',
  BLOCK_OVERLAP: 'El snap dejaría la unidad solapando otra.',
  BLOCK_DEFER_ROW: 'Snap delegado al módulo de fila más cercano.',
  BLOCK_PENETRATION: 'Solape por cara prohibida bloqueado en arrastre.',
  BLOCK_COLLIDE_NO_FACE: 'collideItems no resolvió cara de snap.',
  BLOCK_SNAP_POSITION: 'Cara válida pero falló el cálculo de posición.',
}

const diagnosisThrottle = new Map<string, string>()
const diagnosisRing: RinconeroSnapDiagnosis[] = []
const DIAGNOSIS_RING_MAX = 40
const DIAGNOSIS_THROTTLE_MAX = 250

export function clearRinconeroSnapDiagnosisCache() {
  diagnosisThrottle.clear()
  lastFaceLogKey = ''
}

export function getRinconeroSnapDiagnosisHistory(): readonly RinconeroSnapDiagnosis[] {
  return diagnosisRing
}

export function diagnoseStraightModuleToRinconero(
  module: RinconeroUnitLike,
  rinconero: RinconeroUnitLike,
  moduleWorldPos: Vector3,
  moduleWidth: number,
  context?: {
    distance2D?: number
    intersects?: boolean
    softSnapThresholdMm?: number
    snapReachMm?: number
    triggerCollide?: boolean
  },
): RinconeroSnapDiagnosis {
  const formatMm = (value: number) => (Number.isFinite(value) ? Number(value.toFixed(0)) : null)
  const metrics: Record<string, unknown> = {
    modulePos: formatDragPos3(moduleWorldPos),
    moduleWidth: Number(moduleWidth.toFixed(0)),
    rinconeroCatalog: rinconero.catalogID,
    moduleCatalog: module.catalogID,
    openLateralSide: rinconeroRowLateralSide(rinconero),
    handedness: isRinconeroIzquierda(rinconero.catalogID, rinconeroHandednessFromItem(rinconero))
      ? 'izq'
      : 'der',
  }
  if (context?.distance2D != null) metrics.distance2D = formatMm(context.distance2D)
  if (context?.intersects != null) metrics.intersects = context.intersects
  if (context?.softSnapThresholdMm != null) {
    metrics.softSnapMm = formatMm(context.softSnapThresholdMm)
  }
  if (context?.snapReachMm != null) metrics.snapReachMm = formatMm(context.snapReachMm)

  const base = (
    verdict: RinconeroSnapDiagnosis['verdict'],
    reason: RinconeroSnapReasonCode,
    phase: RinconeroSnapDiagnosisPhase = 'drag-gate',
  ): RinconeroSnapDiagnosis => ({
    flow: 'module-to-rinconero',
    phase,
    verdict,
    reason,
    hint: RINCONERO_SNAP_HINTS[reason],
    moduleCatalog: module.catalogID,
    rinconeroCatalog: rinconero.catalogID,
    metrics,
  })

  if (!rinconero.matrix) return base('block', 'BLOCK_NO_MATRIX')

  const rinPos = new Vector3()
  rinconero.matrix.decompose(rinPos, new Quaternion(), new Vector3())
  const moduleRel = moduleRelativeToRinconero(rinPos, moduleWorldPos)
  metrics.approach = formatApproachFromRel(moduleRel, rinconero)

  if (isApproachingRinconeroInterior(moduleRel, rinconero)) {
    return base('block', 'BLOCK_INTERIOR_L')
  }
  if (isApproachingCushionedRinconeroFace(moduleRel, rinconero)) {
    return base('block', 'BLOCK_CUSHIONED_FACE')
  }

  const canLateral = isExteriorLateralApproachForDebug(moduleRel, rinconero)
  const canLeg = isExteriorLegApproachForDebug(moduleRel, rinconero)
  metrics.canLateral = canLateral
  metrics.canLeg = canLeg
  if (!canLateral && !canLeg) return base('block', 'BLOCK_NO_OPEN_HEMISPHERE')

  const face = resolveRinconeroSnapFaceFromApproach(
    moduleRel,
    rinconero,
    moduleWidth,
    moduleWorldPos,
    { suppressFaceLog: true },
  )
  if (!face) return base('block', 'BLOCK_COLLIDE_NO_FACE', 'collide')
  metrics.resolvedFace = face

  const { minDist } = estimateModuleToRinconeroSnapDistance(rinconero, moduleWidth, moduleWorldPos)
  const faceDistances = computeRinconeroSnapFaceDistances(rinconero, moduleWidth, moduleWorldPos)
  metrics.distToSnapTarget = formatMm(minDist)
  metrics.distLateral = formatMm(faceDistances.distLateral)
  metrics.distLeg = formatMm(faceDistances.distLeg)

  const reach = context?.snapReachMm ?? dynamicRinconeroSnapReachMm(rinconero, moduleWidth)
  metrics.snapReachMm = formatMm(reach)
  if (!Number.isFinite(minDist) || minDist > reach) return base('block', 'BLOCK_DISTANCE')
  if (context?.triggerCollide === false) return base('block', 'BLOCK_OBB_NO_TRIGGER')

  return base('allow', face === 'leg' ? 'OK_SNAP_LEG' : 'OK_SNAP_LATERAL')
}

export function emitRinconeroSnapDiagnosis(
  diagnosis: RinconeroSnapDiagnosis & { moduleId?: string; rinconeroId?: string },
) {
  const throttleKey = [
    diagnosis.moduleId ?? '',
    diagnosis.rinconeroId ?? '',
    diagnosis.phase,
    diagnosis.reason,
  ].join('|')
  const payloadKey = JSON.stringify(diagnosis.metrics)
  if (diagnosisThrottle.get(throttleKey) === payloadKey) return
  diagnosisThrottle.set(throttleKey, payloadKey)
  if (diagnosisThrottle.size > DIAGNOSIS_THROTTLE_MAX) diagnosisThrottle.clear()
  diagnosisRing.push(diagnosis)
  if (diagnosisRing.length > DIAGNOSIS_RING_MAX) diagnosisRing.shift()
  if (!isRinconeroSnapDebug()) return
  const icon = diagnosis.verdict === 'snap' ? '✓' : diagnosis.verdict === 'allow' ? '→' : '✗'
  logRinconeroSnap('diagnosis', `${icon} ${diagnosis.reason}`, {
    flow: diagnosis.flow,
    phase: diagnosis.phase,
    verdict: diagnosis.verdict,
    hint: diagnosis.hint,
    moduleId: diagnosis.moduleId,
    rinconeroId: diagnosis.rinconeroId,
    moduleCatalog: diagnosis.moduleCatalog,
    rinconeroCatalog: diagnosis.rinconeroCatalog,
    ...diagnosis.metrics,
  })
}

if (typeof window !== 'undefined') {
  const w = window as Window & {
    __rinconeroSnapHistory?: () => readonly RinconeroSnapDiagnosis[]
    __enableRinconeroSnapDebug?: () => void
  }
  w.__rinconeroSnapHistory = getRinconeroSnapDiagnosisHistory
  w.__enableRinconeroSnapDebug = enableRinconeroSnapDebug
}

/** Log estructurado: [RinconeroSnap][fase] paso — payload */
export function logRinconeroSnap(
  phase: RinconeroSnapLogPhase,
  step: string,
  data?: Record<string, unknown>,
) {
  if (!isRinconeroSnapDebug()) return
  console.warn(`[RinconeroSnap][${phase}] ${step}`, {
    n: ++rinconeroSnapLogSeq,
    ms: Math.round(performance.now()),
    ...(data ?? {}),
  })
}

export function formatApproachFromRel(
  moduleRel: Vector3,
  rinconero: RinconeroUnitLike,
): Record<string, number | boolean | string> {
  if (!rinconero.matrix) return { error: 'no-matrix' } as Record<string, never>
  const quat = new Quaternion()
  rinconero.matrix.decompose(new Vector3(), quat, new Vector3())
  const { xProj, zProj } = rinconeroApproachProjections(moduleRel, quat)
  const izq = isRinconeroIzquierda(rinconero.catalogID, rinconeroHandednessFromItem(rinconero))
  return {
    xProj: Number(xProj.toFixed(0)),
    zProj: Number(zProj.toFixed(0)),
    interior: isApproachingRinconeroInterior(moduleRel, rinconero),
    canLateral: isExteriorLateralApproachForDebug(moduleRel, rinconero),
    canLeg: isExteriorLegApproachForDebug(moduleRel, rinconero),
    handedness: izq ? 'izq' : 'der',
  }
}

function isExteriorLateralApproachForDebug(
  moduleRelativeToRinconero: Vector3,
  rinconero: RinconeroUnitLike,
): boolean {
  if (!rinconero.matrix || isApproachingRinconeroInterior(moduleRelativeToRinconero, rinconero)) {
    return false
  }
  const quat = new Quaternion()
  rinconero.matrix.decompose(new Vector3(), quat, new Vector3())
  const { xProj, zProj } = rinconeroApproachProjections(moduleRelativeToRinconero, quat)
  const absX = Math.abs(xProj)
  const absZ = Math.abs(zProj)
  const fondo = rinconeroPlanFondo(rinconero)
  if (absX >= absZ * 0.42) return true
  const rinconeroWidth = rinconeroPlanWidth(rinconero)
  const izq = isRinconeroIzquierda(rinconero.catalogID, rinconeroHandednessFromItem(rinconero))
  const onLateralRowSide = izq ? xProj > -rinconeroWidth * 0.2 : xProj < rinconeroWidth * 0.2
  if (onLateralRowSide && absZ < fondo * 0.9) return true
  if (Math.abs(xProj) > rinconeroWidth * 0.2 && absZ < fondo * 1.35) return true
  return false
}

function isExteriorLegApproachForDebug(
  moduleRelativeToRinconero: Vector3,
  rinconero: RinconeroUnitLike,
): boolean {
  return isExteriorLegApproach(moduleRelativeToRinconero, rinconero)
}

export type RinconeroFaceOccupancy = {
  rinconeroId: string
  catalog: unknown
  lateralAfter: boolean
  lateralBefore: boolean
  legOccupied: boolean
  bothExteriorFacesOccupied: boolean
  neighbors: Array<{ id: string; catalog: unknown; nearestFace: string; distMm: number }>
}

const OCCUPANCY_SNAP_RADIUS_MM = 520

/**
 * Detecta si el rinconero ya tiene módulos en lateral (fila) y en pata (L).
 * Escenario problemático: bothExteriorFacesOccupied === true
 */
export function inspectRinconeroFaceOccupancy(
  rinconeroId: string,
  rinconero: RinconeroUnitLike,
  items: Map<
    string,
    { type?: string; catalogID?: unknown; matrix?: RinconeroUnitLike['matrix']; width?: number }
  >,
  excludeIds: string[] = [],
): RinconeroFaceOccupancy {
  const exclude = new Set(excludeIds.map(String))
  const result: RinconeroFaceOccupancy = {
    rinconeroId,
    catalog: rinconero.catalogID,
    lateralAfter: false,
    lateralBefore: false,
    legOccupied: false,
    bothExteriorFacesOccupied: false,
    neighbors: [],
  }
  if (!rinconero.matrix) return result

  const moduleWidth = rinconeroPlanWidth(rinconero) || 600
  const lateral = computeBothModuleLateralSnapPositions(rinconero, moduleWidth)
  const legMatrix = composeMatrixModuleOnRinconeroLeg(rinconero, moduleWidth)
  const legPos = new Vector3()
  if (legMatrix) legMatrix.decompose(legPos, new Quaternion(), new Vector3())

  const faceTargets: Array<{ key: string; pos: Vector3 | null }> = [
    { key: 'lateralAfter', pos: lateral?.after ?? null },
    { key: 'lateralBefore', pos: lateral?.before ?? null },
    { key: 'leg', pos: legMatrix ? legPos : null },
  ]

  items.forEach((candidate, candidateId) => {
    const cid = String(candidateId)
    if (exclude.has(cid) || cid === rinconeroId) return
    if (candidate?.type !== 'linkedItem' || !candidate.matrix) return

    const cPos = new Vector3()
    candidate.matrix.decompose(cPos, new Quaternion(), new Vector3())
    let nearestFace = ''
    let minDist = Number.POSITIVE_INFINITY

    for (const { key, pos } of faceTargets) {
      if (!pos) continue
      const d = Math.hypot(cPos.x - pos.x, cPos.z - pos.z)
      if (d < minDist) {
        minDist = d
        nearestFace = key
      }
    }

    if (minDist > OCCUPANCY_SNAP_RADIUS_MM) return

    result.neighbors.push({
      id: cid,
      catalog: candidate.catalogID,
      nearestFace,
      distMm: Number(minDist.toFixed(0)),
    })

    if (nearestFace === 'lateralAfter') result.lateralAfter = true
    if (nearestFace === 'lateralBefore') result.lateralBefore = true
    if (nearestFace === 'leg') result.legOccupied = true
  })

  const hasLateral = result.lateralAfter || result.lateralBefore
  result.bothExteriorFacesOccupied = hasLateral && result.legOccupied

  if (isRinconeroSnapDebug() && result.bothExteriorFacesOccupied) {
    logRinconeroSnap('occupancy', 'rinconero-con-unidades-en-L-y-fila', { ...result })
  }

  return result
}

export function rinconeroOpenLateralFaceKey(
  rinconero: RinconeroUnitLike,
): 'lateralAfter' | 'lateralBefore' {
  return rinconeroRowLateralSide(rinconero) === 'after' ? 'lateralAfter' : 'lateralBefore'
}

/** ¿La cara exterior (fila o pata) está libre para recibir un módulo? */
export function isRinconeroSnapFaceFree(
  rinconeroId: string,
  rinconero: RinconeroUnitLike,
  face: RinconeroSnapFace,
  items: Map<
    string,
    { type?: string; catalogID?: unknown; matrix?: RinconeroUnitLike['matrix']; width?: number }
  >,
  excludeIds: string[] = [],
): boolean {
  const occ = inspectRinconeroFaceOccupancy(rinconeroId, rinconero, items, excludeIds)
  if (face === 'leg') return !occ.legOccupied
  const lateralKey = rinconeroOpenLateralFaceKey(rinconero)
  return lateralKey === 'lateralAfter' ? !occ.lateralAfter : !occ.lateralBefore
}

/**
 * ¿El solape OBB es con una unidad que ocupa la MISMA cara de snap?
 * Los contactos en esquina (módulo en fila vs snap en pata) son válidos en configuradores IKEA.
 */
export function isRinconeroSnapOverlapOnSameFace(
  rinconeroId: string,
  rinconero: RinconeroUnitLike,
  snapFace: RinconeroSnapFace,
  blockedByUnitId: string,
  items: Map<
    string,
    { type?: string; catalogID?: unknown; matrix?: RinconeroUnitLike['matrix']; width?: number }
  >,
): boolean {
  const occ = inspectRinconeroFaceOccupancy(rinconeroId, rinconero, items, [])
  const neighbor = occ.neighbors.find((n) => n.id === String(blockedByUnitId))
  if (!neighbor) return true
  if (snapFace === 'leg') return neighbor.nearestFace === 'leg'
  return neighbor.nearestFace === rinconeroOpenLateralFaceKey(rinconero)
}

/**
 * Elige la cara de acercamiento preferida; si está ocupada, la alternativa libre
 * (misma regla que configuradores modulares comerciales: 2 anclajes exteriores).
 */
export function resolveVacantRinconeroSnapFace(
  moduleRelativeToRinconero: Vector3,
  rinconero: RinconeroUnitLike,
  moduleWidth: number,
  moduleWorldPos: Vector3,
  rinconeroId: string,
  items: Map<
    string,
    { type?: string; catalogID?: unknown; matrix?: RinconeroUnitLike['matrix']; width?: number }
  >,
  excludeIds: string[] = [],
): RinconeroSnapFace | null {
  const preferred = resolveRinconeroSnapFaceFromApproach(
    moduleRelativeToRinconero,
    rinconero,
    moduleWidth,
    moduleWorldPos,
  )
  if (!preferred) return null
  if (isRinconeroSnapFaceFree(rinconeroId, rinconero, preferred, items, excludeIds)) {
    return preferred
  }
  const alternate: RinconeroSnapFace = preferred === 'leg' ? 'lateral' : 'leg'
  const alternateApproachOk =
    alternate === 'lateral'
      ? isExteriorLateralApproach(moduleRelativeToRinconero, rinconero)
      : isExteriorLegApproach(moduleRelativeToRinconero, rinconero)
  if (
    alternateApproachOk &&
    isRinconeroSnapFaceFree(rinconeroId, rinconero, alternate, items, excludeIds)
  ) {
    return alternate
  }
  return preferred
}

export function formatDragPos3(
  source: Vector3 | { x?: number; y?: number; z?: number; rotation?: number },
): { x: number; y: number; z: number; rotation?: number } {
  const x = Number((source as Vector3).x ?? (source as { x?: number }).x ?? 0)
  const y = Number((source as Vector3).y ?? (source as { y?: number }).y ?? 0)
  const z = Number((source as Vector3).z ?? (source as { z?: number }).z ?? 0)
  const rotation = (source as { rotation?: number }).rotation
  return {
    x: Number(x.toFixed(1)),
    y: Number(y.toFixed(1)),
    z: Number(z.toFixed(1)),
    ...(rotation != null ? { rotation: Number(rotation.toFixed(1)) } : {}),
  }
}

export function formatDragPosFromMatrix(matrix: Matrix4) {
  const pos = new Vector3()
  const quat = new Quaternion()
  matrix.decompose(pos, quat, new Vector3())
  const eulerY = (Math.atan2(2 * quat.y * quat.w, 1 - 2 * quat.y * quat.y) * 180) / Math.PI
  return { ...formatDragPos3(pos), rotation: Number(eulerY.toFixed(1)) }
}

const logRinconeroDraw = (step: string, payload?: Record<string, unknown>) => {
  if (!RINCONERO_DRAW_DEBUG) return
  console.warn('[RinconeroDraw]', step, payload ?? {})
}

if (RINCONERO_DRAW_DEBUG && typeof window !== 'undefined') {
  console.warn(
    '[RinconeroDraw] Depuración activa — filtra consola por "RinconeroDraw". Atemporal usa Toolbar/index.tsx.',
  )
}

export function normalizeCatalogCode(catalogID: unknown): string {
  return String(catalogID || '')
    .trim()
    .toLowerCase()
}

/** Código base sin sufijos de acabado (evita que « Li» active izquierda). */
export function rinconeroBaseCatalogCode(catalogID: unknown): string {
  return normalizeCatalogCode(catalogID)
    .replace(ACABADO_SUFFIX_REGEX, '')
    .replace(/\s+go$/i, '')
    .trim()
}

/** Rinconero Atemporal: referencias con prefijo 166- (ej. 166-95-95). */
export function isCatalog166Series(catalogID: unknown): boolean {
  const base = rinconeroBaseCatalogCode(catalogID)
  return base.startsWith('166-') || /^166\d/.test(base)
}

export function isOreja(catalogID: unknown): boolean {
  const base = rinconeroBaseCatalogCode(catalogID)
  return (
    base.startsWith('177-') ||
    base.startsWith('178-') ||
    base.startsWith('179-') ||
    base.startsWith('180-') ||
    /^(177|178|179|180)\d/.test(base)
  )
}

export function isPouff(catalogID: unknown): boolean {
  const base = rinconeroBaseCatalogCode(catalogID)
  return (
    base.startsWith('186-') ||
    base.startsWith('187-') ||
    base.startsWith('188-') ||
    /^(186|187|188)\d/.test(base)
  )
}

/**
 * Módulo del producto KUBE: sus códigos de catálogo empiezan por «K» + dígito
 * (ej. K10BA, K1RB, K2RB), a diferencia de los de Atemporal (numéricos o «BR»).
 *
 * Estos módulos deben colisionar/snapear como los puffs (a 4 lados, sin solape)
 * y NO deben usar el snap lateral en fila de módulos Atemporal.
 */
export function isKubeModule(catalogID: unknown): boolean {
  return /^k\d/i.test(String(catalogID ?? '').trim())
}

/**
 * Cojín KUBE: código + exactamente 2 medidas (ej. "K1C 040 040").
 * Bases/respaldos llevan 3 (ej. "K10BA 060 140 035", "K1RB 070 040 051").
 * Se dibujan desde COJINESMENU y se colocan libres / encima de bases.
 */
export function isKubeCushion(catalogID: unknown): boolean {
  const s = String(catalogID ?? '').trim()
  return /^K\d+[A-Z]*\s+\d+\s+\d+\s*$/i.test(s)
}

/**
 * Base/asiento KUBE (menú BASE): código con BA (ej. "K10BA 060 140 035").
 */
export function isKubeBase(catalogID: unknown): boolean {
  return /^K\d+BA\b/i.test(String(catalogID ?? '').trim())
}

/**
 * Respaldo KUBE (menú RESPALDOS): código con RB (ej. "K1RB 070 040 051").
 * Colisión lateral del cojín usa su ancho × alto × fondo reales.
 */
export function isKubeRespaldo(catalogID: unknown): boolean {
  return /^K\d+RB\b/i.test(String(catalogID ?? '').trim())
}

/** Módulo KUBE que snapea en planta (bases/respaldos); excluye cojines. */
export function isKubeSnapModule(catalogID: unknown): boolean {
  return isKubeModule(catalogID) && !isKubeCushion(catalogID)
}

/** Alias de isCatalog166Series. */
export function isRinconeroCatalog(catalogID: unknown): boolean {
  return isCatalog166Series(catalogID)
}

/** Corner XL con mano D/I (181-185). */
export function isCornerXLHandedCatalog(catalogID: unknown): boolean {
  const base = rinconeroBaseCatalogCode(catalogID)
  if (!/^(181|182|183|184|185)-/.test(base)) return false
  return unitArmHandednessLetter(catalogID) != null
}

/** Esquineros L con snap tipo rinconero (166 + corner XL). */
export function isModularCornerSnapCatalog(catalogID: unknown): boolean {
  return isRinconeroCatalog(catalogID) || isCornerXLHandedCatalog(catalogID)
}

function isLeftHandFromMano(context?: RinconeroHandednessContext): boolean {
  const hand = String(context?.hand ?? context?.mano ?? '').toLowerCase()
  return hand.includes('cd') || hand.includes('izq') || hand === 'i'
}

/** Izquierdo en referencia 166-: 166i / 166-i o último segmento con «i» (ej. 166-95-95i). */
function is166SeriesLeftCatalog(base: string): boolean {
  if (/\b166i\b/.test(base) || /166-i/.test(base) || /166i-/.test(base)) return true
  const lastSegment = base.split('-').pop() || ''
  return lastSegment.length > 1 && /i$/i.test(lastSegment)
}

/** Rinconero izquierdo (166-95-95i, mano cd/izq, …). 166-95-95 → derecho.
 *  Corner XL: «… D» → snap como rinconero izq; «… I» → como rinconero der. */
export function isRinconeroIzquierda(
  catalogID: unknown,
  context?: RinconeroHandednessContext,
): boolean {
  if (isCornerXLHandedCatalog(catalogID)) {
    const letter = unitArmHandednessLetter(catalogID)
    if (letter === 'd') return true
    if (letter === 'i') return false
    return false
  }
  if (!isRinconeroCatalog(catalogID)) return false
  if (isLeftHandFromMano(context)) return true
  return is166SeriesLeftCatalog(rinconeroBaseCatalogCode(catalogID))
}

/** Rinconero derecho (166-95-95 por defecto). */
export function isRinconeroDerecha(
  catalogID: unknown,
  context?: RinconeroHandednessContext,
): boolean {
  if (!isModularCornerSnapCatalog(catalogID)) return false
  return !isRinconeroIzquierda(catalogID, context)
}

export function rinconeroHandednessFromItem(item?: {
  catalogID?: unknown
  hand?: unknown
  mano?: unknown
}): RinconeroHandednessContext {
  return { hand: item?.hand, mano: item?.mano }
}

/** Ancho de un módulo linked (morph Ancho o width). Sofás modulares — sin lógica de encimeras. */
export function resolveLinkedModuleWidth(item?: {
  width?: number
  morphs?: Array<{ name?: string; default?: number; _default?: number }>
}): number {
  return modulePlanWidth(item as RinconeroUnitLike)
}

export function getRegletaInvertedValue(
  value: number,
  morph: { min?: number; max?: number } | null | undefined,
) {
  if (!morph) return value
  if (value >= morph.max!) return morph.min!
  if (value <= morph.min!) return morph.max!
  return morph.max! + morph.min! - value
}

export function rinconeroSideOffsetFromReference(referenceItem?: RinconeroUnitLike) {
  const regletaMorph = referenceItem?.morphs?.find((m) => m.name === 'Regleta')
  const regletaValue = referenceItem?.regleta || 0
  const regletaInverted = getRegletaInvertedValue(regletaValue, regletaMorph)
  return (referenceItem?.thickness || 0) + regletaInverted
}

/** Ancho en mm desde código Atemporal (ej. 165-272-95 → 272 cm → 2720 mm). */
export function widthFromAtemporalCatalogCode(catalogID: unknown): number {
  const parts = String(catalogID || '')
    .trim()
    .split('-')
    .filter(Boolean)
  if (parts.length < 2) return 0
  const segment = Number(parts[1])
  if (!Number.isFinite(segment) || segment <= 0) return 0
  return segment < 1000 ? segment * 10 : segment
}

/** Ancho por defecto del catálogo a dibujar (morph Ancho, backGLBInfo o código). */
export function defaultWidthForDrawCatalog(
  catalogID: unknown,
  sceneGLB?: Map<
    string | number,
    {
      backGLBInfo?: {
        morphs?: Array<{ name?: string; default?: number; _default?: number }>
        width?: number
      }
    }
  >,
  fallback = 0,
): number {
  const fromCode = widthFromAtemporalCatalogCode(catalogID)
  if (fromCode > 0) return fromCode
  if (!catalogID || !sceneGLB) return fallback
  const entry = sceneGLB.get(String(catalogID))
  const morphs = entry?.backGLBInfo?.morphs
  const widthMorph = morphs?.find((m) => m.name === 'Ancho' || m.name === 'width')
  const fromMorph = widthMorph?.default ?? widthMorph?._default
  if (fromMorph != null && Number(fromMorph) > 0) return Number(fromMorph)
  const fromInfo = entry?.backGLBInfo?.width
  if (fromInfo != null && Number(fromInfo) > 0) return Number(fromInfo)
  return fallback
}

/** Ancho (eje X local) del rinconero 166- desde morph o width. */
export function rinconeroPlanWidth(rinconero?: RinconeroUnitLike): number {
  const widthMorph = rinconero?.morphs?.find((m) => m.name === 'Ancho' || m.name === 'width')
  const fromMorph =
    widthMorph != null
      ? Number(
          (widthMorph as { default?: number; _default?: number }).default ??
            (widthMorph as { _default?: number })._default,
        )
      : NaN
  if (Number.isFinite(fromMorph) && fromMorph > 0) return fromMorph
  return Number(rinconero?.width || 0)
}

/** Alias de rinconeroLegDepthOffset (fondo / thickness del plano). */
export function rinconeroPlanFondo(rinconero?: RinconeroUnitLike): number {
  return rinconeroLegDepthOffset(rinconero)
}

/** Ancho de un módulo recto (misma lógica que rinconeroPlanWidth). */
export function modulePlanWidth(module?: RinconeroUnitLike): number {
  return rinconeroPlanWidth(module)
}

/** Signo del eje Z local hacia la línea de respaldo (166- derecho → +Z). */
export function rinconeroFondoAxisSign(
  catalogID: unknown,
  context?: RinconeroHandednessContext,
): number {
  return isRinconeroIzquierda(catalogID, context) ? -1 : 1
}

/**
 * Desplazamiento local: origen interior del GLB → esquina exterior del respaldo (gizmo).
 * Esquina = origen + ejeX×ancho + ejeZ×fondo (donde convergen los respaldos exteriores).
 */
export function rinconeroExteriorPivotLocalOffset(rinconero: RinconeroUnitLike): Vector3 {
  const hand = rinconeroHandednessFromItem(rinconero)
  const izq = isRinconeroIzquierda(rinconero.catalogID, hand)
  const w = rinconeroPlanWidth(rinconero)
  const f = rinconeroPlanFondo(rinconero)
  const zSign = rinconeroFondoAxisSign(rinconero.catalogID, hand)
  if (!rinconero.matrix) {
    return izq ? new Vector3(-w, 0, -zSign * f) : new Vector3(w, 0, zSign * f)
  }

  const interiorPos = new Vector3()
  const interiorQuat = new Quaternion()
  rinconero.matrix.decompose(interiorPos, interiorQuat, new Vector3())

  const xAxis = new Vector3(1, 0, 0).applyQuaternion(interiorQuat)
  const zAxis = new Vector3(0, 0, 1).applyQuaternion(interiorQuat)
  const cornerWorld = interiorPos
    .clone()
    .add(xAxis.multiplyScalar(izq ? -w : w))
    .add(zAxis.multiplyScalar(zSign * f))

  return cornerWorld.sub(interiorPos).applyQuaternion(interiorQuat.clone().invert())
}

export function rinconeroPivotMatrixFromInterior(
  interiorMatrix: Matrix4,
  rinconero: RinconeroUnitLike,
): Matrix4 {
  if (!isModularCornerSnapCatalog(rinconero.catalogID)) return interiorMatrix.clone()
  const offset = rinconeroExteriorPivotLocalOffset(rinconero)
  const result = interiorMatrix.clone()
  const position = new Vector3()
  const quaternion = new Quaternion()
  const scale = new Vector3()
  result.decompose(position, quaternion, scale)
  position.add(offset.clone().applyQuaternion(quaternion))
  result.compose(position, quaternion, scale)
  return result
}

export function rinconeroInteriorMatrixFromPivot(
  pivotMatrix: Matrix4,
  rinconero: RinconeroUnitLike,
): Matrix4 {
  if (!isModularCornerSnapCatalog(rinconero.catalogID)) return pivotMatrix.clone()
  const offset = rinconeroExteriorPivotLocalOffset(rinconero)
  const result = pivotMatrix.clone()
  const position = new Vector3()
  const quaternion = new Quaternion()
  const scale = new Vector3()
  result.decompose(position, quaternion, scale)
  position.sub(offset.clone().applyQuaternion(quaternion))
  result.compose(position, quaternion, scale)
  return result
}

/** Punto de referencia en la línea de respaldo: origen + ejeZ × fondo. */
export function rinconeroBackPlanePoint(rinconero: RinconeroUnitLike): Vector3 | null {
  if (!rinconero.matrix) return null
  const position = new Vector3()
  const quaternion = new Quaternion()
  rinconero.matrix.decompose(position, quaternion, new Vector3())
  const hand = rinconeroHandednessFromItem(rinconero)
  const zAxis = new Vector3(0, 0, 1).applyQuaternion(quaternion)
  const fondoSign = rinconeroFondoAxisSign(rinconero.catalogID, hand)
  return position.clone().add(zAxis.multiplyScalar(fondoSign * rinconeroPlanFondo(rinconero)))
}

export type RinconeroSnapFace = 'lateral' | 'leg'

const SNAP_ROTATION_MATCH_RAD = 0.42

function unitQuat(unit: RinconeroUnitLike): Quaternion | null {
  if (!unit.matrix) return null
  const q = new Quaternion()
  unit.matrix.decompose(new Vector3(), q, new Vector3())
  return q
}

/** Misma orientación que el rinconero (cara fila / lateral). */
export function isLateralRotationToRinconero(
  module: RinconeroUnitLike,
  rinconero: RinconeroUnitLike,
): boolean {
  const mq = unitQuat(module)
  const rq = unitQuat(rinconero)
  if (!mq || !rq) return false
  return mq.angleTo(rq) < SNAP_ROTATION_MATCH_RAD
}

/** Orientación de la pata en L (1.º, 2.º o 4.º rinconero del cuadrado). */
export function isLegRotationToRinconero(
  module: RinconeroUnitLike,
  rinconero: RinconeroUnitLike,
  legChainSteps: 1 | 2 | 3 = 1,
): boolean {
  const mq = unitQuat(module)
  if (!mq) return false
  const legMatrix = composeMatrixOnRinconeroByLegChain(rinconero, legChainSteps, 0)
  if (!legMatrix) return false
  const legQ = new Quaternion()
  legMatrix.decompose(new Vector3(), legQ, new Vector3())
  return mq.angleTo(legQ) < SNAP_ROTATION_MATCH_RAD
}

/**
 * Esquina interior del L (sin snap).
 * El lado izquierdo (x<0, z≈0) es fila válida; no confundir con interior.
 */
export function isApproachingRinconeroInterior(
  moduleRelativeToRinconero: Vector3,
  rinconero: RinconeroUnitLike,
): boolean {
  if (!rinconero.matrix) return false
  const quat = new Quaternion()
  rinconero.matrix.decompose(new Vector3(), quat, new Vector3())
  const { xProj, zProj } = rinconeroApproachProjections(moduleRelativeToRinconero, quat)
  const absX = Math.abs(xProj)
  const absZ = Math.abs(zProj)
  const fondo = rinconeroPlanFondo(rinconero)
  if (absX > absZ * 0.85) return false
  if (absZ < fondo * 0.35 && absX > fondo * 0.15) return false
  const izq = isRinconeroIzquierda(rinconero.catalogID, rinconeroHandednessFromItem(rinconero))
  if (izq) return xProj > fondo * 0.15 && zProj > fondo * 0.15
  return xProj < -fondo * 0.25 && zProj < -fondo * 0.35
}

export function rinconeroRowLateralSide(rinconero: RinconeroUnitLike): 'after' | 'before' {
  const izq = isRinconeroIzquierda(rinconero.catalogID, rinconeroHandednessFromItem(rinconero))
  return izq ? 'after' : 'before'
}

export function rinconeroCushionedLateralSide(rinconero: RinconeroUnitLike): 'after' | 'before' {
  return rinconeroRowLateralSide(rinconero) === 'after' ? 'before' : 'after'
}

function rinconeroCushionedBackHemisphere(rinconero: RinconeroUnitLike): 'positiveZ' | 'negativeZ' {
  const izq = isRinconeroIzquierda(rinconero.catalogID, rinconeroHandednessFromItem(rinconero))
  return izq ? 'positiveZ' : 'negativeZ'
}

export function isApproachingCushionedRinconeroFace(
  moduleRelativeToRinconero: Vector3,
  rinconero: RinconeroUnitLike,
): boolean {
  if (!rinconero.matrix) return true
  if (isApproachingRinconeroInterior(moduleRelativeToRinconero, rinconero)) return true
  const quat = new Quaternion()
  rinconero.matrix.decompose(new Vector3(), quat, new Vector3())
  const { xProj, zProj } = rinconeroApproachProjections(moduleRelativeToRinconero, quat)
  const width = rinconeroPlanWidth(rinconero)
  const fondo = rinconeroPlanFondo(rinconero)
  const absX = Math.abs(xProj)
  const absZ = Math.abs(zProj)
  const cushionedLateral = rinconeroCushionedLateralSide(rinconero)
  const onCushionedLateral =
    cushionedLateral === 'after' ? xProj > width * 0.1 : xProj < -width * 0.1
  const cushionedBack = rinconeroCushionedBackHemisphere(rinconero)
  const onCushionedBack = cushionedBack === 'positiveZ' ? zProj > fondo * 0.1 : zProj < -fondo * 0.1
  if (onCushionedLateral && absX >= absZ * 0.42) return true
  if (onCushionedBack && absZ >= absX * 0.42) return true
  return false
}

function isExteriorLateralApproach(
  moduleRelativeToRinconero: Vector3,
  rinconero: RinconeroUnitLike,
): boolean {
  if (!rinconero.matrix || isApproachingRinconeroInterior(moduleRelativeToRinconero, rinconero)) {
    return false
  }
  if (isApproachingCushionedRinconeroFace(moduleRelativeToRinconero, rinconero)) {
    return false
  }
  const quat = new Quaternion()
  rinconero.matrix.decompose(new Vector3(), quat, new Vector3())
  const { xProj, zProj } = rinconeroApproachProjections(moduleRelativeToRinconero, quat)
  const absX = Math.abs(xProj)
  const absZ = Math.abs(zProj)
  const fondo = rinconeroPlanFondo(rinconero)
  const rinconeroWidth = rinconeroPlanWidth(rinconero)
  const validSide = rinconeroRowLateralSide(rinconero)
  const rowDominant = Math.abs(xProj) >= Math.abs(zProj) * 0.55
  const onOpenLateralHemisphere =
    validSide === 'after'
      ? xProj > (rowDominant ? 0 : rinconeroWidth * 0.06)
      : xProj < (rowDominant ? 0 : -rinconeroWidth * 0.06)
  if (!onOpenLateralHemisphere) return false
  if (absX >= absZ * 0.38) return true
  if (absZ < fondo * 0.65 && absX > rinconeroWidth * 0.1) return true
  return false
}

function isExteriorLegApproach(
  moduleRelativeToRinconero: Vector3,
  rinconero: RinconeroUnitLike,
): boolean {
  if (!rinconero.matrix || isApproachingRinconeroInterior(moduleRelativeToRinconero, rinconero)) {
    return false
  }
  if (isApproachingCushionedRinconeroFace(moduleRelativeToRinconero, rinconero)) {
    return false
  }
  const quat = new Quaternion()
  rinconero.matrix.decompose(new Vector3(), quat, new Vector3())
  const { xProj, zProj } = rinconeroApproachProjections(moduleRelativeToRinconero, quat)
  const fondo = rinconeroPlanFondo(rinconero)
  const absX = Math.abs(xProj)
  const absZ = Math.abs(zProj)
  const izq = isRinconeroIzquierda(rinconero.catalogID, rinconeroHandednessFromItem(rinconero))
  const onOpenLegHemisphere = izq ? zProj < -fondo * 0.06 : zProj > fondo * 0.06
  if (!onOpenLegHemisphere) return false
  if (absX > absZ * 1.85 && absZ < fondo * 0.4) return false
  return absZ >= absX * 0.38 || absZ > fondo * 0.12
}

/** Distancia 2D (mm) del módulo arrastrado a la posición objetivo del snap rinconero. */
export function distanceToRinconeroSnapTarget(
  rinconero: RinconeroUnitLike,
  moduleWidth: number,
  moduleRelativeToRinconero: Vector3,
  moduleWorldPos: Vector3,
  face: RinconeroSnapFace,
): number {
  if (face === 'leg') {
    const legMatrix = composeMatrixModuleOnRinconeroLeg(rinconero, moduleWidth)
    if (!legMatrix) return Number.POSITIVE_INFINITY
    const legPos = new Vector3()
    legMatrix.decompose(legPos, new Quaternion(), new Vector3())
    return Math.hypot(moduleWorldPos.x - legPos.x, moduleWorldPos.z - legPos.z)
  }
  const lateralPos = computeModuleLateralSnapPosition(
    rinconero,
    moduleWidth,
    moduleRelativeToRinconero,
    moduleWorldPos,
  )
  if (!lateralPos) return Number.POSITIVE_INFINITY
  return Math.hypot(moduleWorldPos.x - lateralPos.x, moduleWorldPos.z - lateralPos.z)
}

/** Cerca del imán: permitir snap salvo que el arrastre vaya claramente hacia afuera. */
export function snapAttemptAllowedByDistance(
  frameMove: Vector3,
  draggedPos: Vector3,
  otherPos: Vector3,
  distance2D: number,
  maxSnapDistance2D: number,
): boolean {
  if (distance2D >= maxSnapDistance2D * 0.88) return false
  const move = frameMove.clone().setY(0)
  if (move.lengthSq() < 9) return false
  const toward = otherPos.clone().sub(draggedPos).setY(0)
  if (toward.lengthSq() < 1) return false
  return move.dot(toward) > 25
}

/**
 * El usuario arrastra alejándose de un punto de snap (posición objetivo o comprometida).
 * Umbral bajo en dot: basta un gesto claro de separación.
 */
export function isDragMovingAwayFromSnapTarget(
  frameMove: Vector3,
  draggedPos: Vector3,
  snapTargetPos: Vector3,
  minDot = 30,
): boolean {
  const move = frameMove.clone().setY(0)
  if (move.lengthSq() < 64) return false
  const away = draggedPos.clone().sub(snapTargetPos).setY(0)
  if (away.lengthSq() < 100) return false
  away.normalize()
  return move.dot(away) > minDot
}

/** Arrastre válido hacia snap 166- (lateral admite acercamiento tangencial por la fila). */
export function isDragMovingToward166Snap(
  frameMove: Vector3,
  draggedPos: Vector3,
  otherPos: Vector3,
  moduleRel: Vector3,
  rinconero: RinconeroUnitLike,
  moduleWidth = 0,
): boolean {
  if (!rinconero.matrix) return false
  const move = frameMove.clone().setY(0)
  if (move.lengthSq() < 64) return false

  const width =
    moduleWidth > 0 ? moduleWidth : rinconeroPlanWidth(rinconero) || Number(rinconero.width || 0)
  const lateralPos = computeModuleLateralSnapPosition(rinconero, width, moduleRel, draggedPos)
  if (lateralPos) {
    if (isDragMovingAwayFromSnapTarget(frameMove, draggedPos, lateralPos, 25)) {
      return false
    }
    const towardLateralSnap = lateralPos.clone().sub(draggedPos).setY(0)
    if (towardLateralSnap.lengthSq() > 1) {
      return move.dot(towardLateralSnap) > 35
    }
  }

  const quat = new Quaternion()
  rinconero.matrix.decompose(new Vector3(), quat, new Vector3())
  const { xProj, zProj } = rinconeroApproachProjections(moduleRel, quat)
  const rinconeroWidth = rinconeroPlanWidth(rinconero)
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(quat)
  const moveX = move.dot(xAxis)

  if (Math.abs(xProj) > rinconeroWidth * 0.15) {
    // Módulo en extensión lateral: hacia el rinconero es moveX opuesto al lado de extensión.
    if (xProj >= 0) return moveX < -35
    return moveX > 35
  }

  const toward = otherPos.clone().sub(draggedPos).setY(0)
  if (toward.lengthSq() > 1 && move.dot(toward) > 40) return true

  if (Math.abs(zProj) <= Math.abs(xProj) * 0.65) return false
  const izq = isRinconeroIzquierda(rinconero.catalogID, rinconeroHandednessFromItem(rinconero))
  const onExteriorLeg = izq ? zProj < 0 : zProj > 0
  if (!onExteriorLeg) return false
  const moveZ = move.dot(new Vector3(0, 0, 1).applyQuaternion(quat))
  return izq ? moveZ < -35 : moveZ > 35
}

export function rinconeroSnapAttemptAllowed(
  frameMove: Vector3,
  draggedPos: Vector3,
  otherPos: Vector3,
  moduleRel: Vector3,
  rinconero: RinconeroUnitLike,
  distance2D: number,
  maxSnapDistance2D: number,
  moduleWidth = 0,
): boolean {
  if (
    snapAttemptAllowedByDistance(frameMove, draggedPos, otherPos, distance2D, maxSnapDistance2D)
  ) {
    return true
  }
  return isDragMovingToward166Snap(
    frameMove,
    draggedPos,
    otherPos,
    moduleRel,
    rinconero,
    moduleWidth,
  )
}

/** Acercamiento válido cuando el rinconero es el arrastrado hacia otra unidad. */
export function rinconeroBeingDraggedSnapAttemptAllowed(
  frameMove: Vector3,
  draggedPos: Vector3,
  targetPos: Vector3,
  approachFromDraggedToTarget: Vector3,
  targetUnit: RinconeroUnitLike,
  distance2D: number,
  maxSnapDistance2D: number,
): boolean {
  if (
    snapAttemptAllowedByDistance(frameMove, draggedPos, targetPos, distance2D, maxSnapDistance2D)
  ) {
    return true
  }
  if (isDragMovingTowardOtherItem(frameMove, draggedPos, targetPos)) return true
  if (!targetUnit.matrix) return false
  const quat = new Quaternion()
  targetUnit.matrix.decompose(new Vector3(), quat, new Vector3())
  const { xProj, zProj } = rinconeroApproachProjections(approachFromDraggedToTarget, quat)
  const move = frameMove.clone().setY(0)
  if (move.lengthSq() < 4) return true
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(quat)
  const zAxis = new Vector3(0, 0, 1).applyQuaternion(quat)
  if (Math.abs(xProj) >= Math.abs(zProj) * 0.45) {
    const moveX = move.dot(xAxis)
    return xProj >= 0 ? moveX > -200 : moveX < 200
  }
  if (Math.abs(zProj) >= Math.abs(xProj) * 0.45) {
    return move.dot(zAxis) > -200
  }
  const toward = targetPos.clone().sub(draggedPos).setY(0)
  return toward.lengthSq() < 1 || move.dot(toward) > -40
}

/** Extremo de fila al acercar rinconero arrastrado a un módulo recto. */
export function resolveUnitLateralAttachEndFromApproach(
  approachFromDraggedToTarget: Vector3,
  unitQuat: Quaternion,
): 'left' | 'right' {
  const { xProj } = rinconeroApproachProjections(approachFromDraggedToTarget, unitQuat)
  // Rinconero en +X local del módulo → pegar al extremo derecho de la fila.
  return xProj > 0 ? 'right' : 'left'
}

export function isRowExtensionApproachToRinconero(
  draggedPos: Vector3,
  draggedItem: RinconeroUnitLike,
  rinconero: RinconeroUnitLike,
  rinconeroPos: Vector3,
): boolean {
  if (!rinconero.matrix || !draggedItem?.matrix) return false
  const rinQuat = new Quaternion()
  rinconero.matrix.decompose(new Vector3(), rinQuat, new Vector3())
  const dragQuat = new Quaternion()
  draggedItem.matrix.decompose(new Vector3(), dragQuat, new Vector3())
  if (dragQuat.angleTo(rinQuat) > SNAP_ROTATION_MATCH_RAD) return false
  const moduleRel = moduleRelativeToRinconero(rinconeroPos, draggedPos)
  const { xProj, zProj } = rinconeroApproachProjections(moduleRel, rinQuat)
  return Math.abs(xProj) >= Math.abs(zProj) * 0.55
}

export function findCloserRowUnitForRinconeroSnap(
  draggedPos: Vector3,
  draggedItem: RinconeroUnitLike,
  rinconero: RinconeroUnitLike,
  rinconeroPos: Vector3,
  items: Map<string, { type?: string; catalogID?: unknown; matrix?: RinconeroUnitLike['matrix'] }>,
  dragTargetId: string,
  moduleWidth = 0,
): string | null {
  if (!rinconero.matrix || !draggedItem?.matrix) return null
  if (!isRowExtensionApproachToRinconero(draggedPos, draggedItem, rinconero, rinconeroPos)) {
    return null
  }

  const width =
    moduleWidth > 0
      ? moduleWidth
      : rinconeroPlanWidth(draggedItem) || Number(draggedItem.width || 0)
  const rinLat = estimateModuleToRinconeroSnapDistance(rinconero, width, draggedPos)
  const rinLatDist =
    rinLat.face === 'lateral' && Number.isFinite(rinLat.minDist)
      ? rinLat.minDist
      : Number.POSITIVE_INFINITY

  const rinQuat = new Quaternion()
  rinconero.matrix.decompose(new Vector3(), rinQuat, new Vector3())
  const zAxis = new Vector3(0, 0, 1).applyQuaternion(rinQuat)
  const openSide = rinconeroRowLateralSide(rinconero)
  const moduleRel = moduleRelativeToRinconero(rinconeroPos, draggedPos)
  const { xProj: dragXProj } = rinconeroApproachProjections(moduleRel, rinQuat)
  const dragAlongRow = openSide === 'after' ? dragXProj : -dragXProj

  let closerUnitId: string | null = null
  let closerRowSnapDist = Number.POSITIVE_INFINITY

  items.forEach((candidate, candidateId) => {
    if (String(candidateId) === String(dragTargetId)) return
    if (candidate?.type !== 'linkedItem' || !candidate.matrix) return
    if (isModularCornerSnapCatalog(candidate.catalogID)) return

    const cPos = new Vector3()
    const cQuat = new Quaternion()
    candidate.matrix.decompose(cPos, cQuat, new Vector3())
    if (cQuat.angleTo(rinQuat) > SNAP_ROTATION_MATCH_RAD) return
    if (Math.abs(cPos.clone().sub(rinconeroPos).dot(zAxis)) > 420) return

    const cRel = moduleRelativeToRinconero(rinconeroPos, cPos)
    const { xProj: cXProj } = rinconeroApproachProjections(cRel, rinQuat)
    const onOpenRowHemisphere = openSide === 'after' ? cXProj > 0 : cXProj < 0
    if (!onOpenRowHemisphere) return

    const candAlongRow = openSide === 'after' ? cXProj : -cXProj
    // Solo redirigir si el arrastrado va a extender la fila MÁS ALLÁ del candidato,
    // no si está rellenando el hueco entre el rinconero y ese módulo.
    if (dragAlongRow <= candAlongRow + 80) return

    const rowSnapDist = straightModuleLateralSnapDistance(draggedItem, candidate, draggedPos, width)
    if (rowSnapDist == null) return
    if (rowSnapDist >= rinLatDist - 50) return
    if (rowSnapDist >= closerRowSnapDist) return

    closerRowSnapDist = rowSnapDist
    closerUnitId = String(candidateId)
  })

  if (closerUnitId) {
    logRinconeroSnap('defer', 'redirigir-snap-al-modulo-de-fila', {
      dragTargetId,
      rinconeroCatalog: rinconero.catalogID,
      closerUnitId,
      distToRinLateral: Number.isFinite(rinLatDist) ? Number(rinLatDist.toFixed(0)) : null,
      distToRowUnit: Number(closerRowSnapDist.toFixed(0)),
      approach: formatApproachFromRel(moduleRel, rinconero),
    })
  }
  return closerUnitId
}

/** Unidad normal en fila: si hay módulo recto más cerca, snap a ese módulo. */
export function shouldDeferRinconeroSnapToRowUnit(
  draggedPos: Vector3,
  draggedItem: RinconeroUnitLike,
  rinconero: RinconeroUnitLike,
  rinconeroPos: Vector3,
  items: Map<string, { type?: string; catalogID?: unknown; matrix?: RinconeroUnitLike['matrix'] }>,
  dragTargetId: string,
): boolean {
  return (
    findCloserRowUnitForRinconeroSnap(
      draggedPos,
      draggedItem,
      rinconero,
      rinconeroPos,
      items,
      dragTargetId,
    ) != null
  )
}

/** Cara más plausible cuando ya estás pegado pero la heurística estricta no eligió cara. */
export function resolveNearestRinconeroSnapFace(
  moduleRelativeToRinconero: Vector3,
  rinconero: RinconeroUnitLike,
): RinconeroSnapFace | null {
  if (!rinconero.matrix) return null
  if (isApproachingRinconeroInterior(moduleRelativeToRinconero, rinconero)) return null
  const quat = new Quaternion()
  rinconero.matrix.decompose(new Vector3(), quat, new Vector3())
  const { xProj, zProj } = rinconeroApproachProjections(moduleRelativeToRinconero, quat)
  const absX = Math.abs(xProj)
  const absZ = Math.abs(zProj)
  const izq = isRinconeroIzquierda(rinconero.catalogID, rinconeroHandednessFromItem(rinconero))
  const rinconeroWidth = rinconeroPlanWidth(rinconero)
  const rinconeroFondo = rinconeroPlanFondo(rinconero)
  const onLateral = izq ? xProj > -rinconeroWidth * 0.4 : xProj < rinconeroWidth * 0.4
  if (onLateral && (absX >= absZ * 0.4 || absX > 160)) return 'lateral'
  const onLeg = izq ? zProj < -rinconeroFondo * 0.06 : zProj > rinconeroFondo * 0.06
  if (onLeg) return 'leg'
  return absX >= absZ ? 'lateral' : 'leg'
}

/**
 * Pasos de pata en cadena desde el rinconero ancla (como dibujar 2.º / 4.º rinconero).
 * 1 = 1ª pata (cuadrante 2), 3 = 4.º rinconero (cuadrante opuesto).
 */
export function resolve166LegChainSteps(
  moduleRelativeToRinconero: Vector3,
  rinconero: RinconeroUnitLike,
): 1 | 2 | 3 {
  if (!rinconero.matrix) return 1
  const quat = new Quaternion()
  rinconero.matrix.decompose(new Vector3(), quat, new Vector3())
  const { xProj, zProj } = rinconeroApproachProjections(moduleRelativeToRinconero, quat)
  const izq = isRinconeroIzquierda(rinconero.catalogID, rinconeroHandednessFromItem(rinconero))
  const zDominant = Math.abs(zProj) >= Math.abs(xProj) * 0.85
  const fourthQuadrant = izq ? zProj > 0 : zProj < 0
  if (zDominant && fourthQuadrant) return 3
  if (zDominant && !fourthQuadrant) return 1
  if (Math.abs(xProj) > Math.abs(zProj)) return 2
  return fourthQuadrant ? 3 : 1
}

/** Matriz tras N patas en L (dibujo 2.º…4.º rinconero o snap por cuadrante). */
export function composeMatrixOnRinconeroByLegChain(
  rinconero: RinconeroUnitLike,
  legSteps: 1 | 2 | 3,
  moduleWidth = 0,
): Matrix4 | null {
  if (!rinconero.matrix) return null
  let unit: RinconeroUnitLike = rinconero
  let result: Matrix4 | null = null
  for (let step = 0; step < legSteps; step++) {
    const width = step === legSteps - 1 ? moduleWidth : 0
    result = composeMatrixModuleOnRinconeroLeg(unit, width)
    if (!result) return null
    unit = {
      catalogID: rinconero.catalogID,
      width: rinconero.width,
      thickness: rinconero.thickness,
      morphs: rinconero.morphs,
      hand: (rinconero as { hand?: unknown }).hand,
      mano: (rinconero as { mano?: unknown }).mano,
      matrix: result,
    }
  }
  return result
}

/**
 * Módulo recto en la pata MÁS CERCANA al punto de arrastre.
 * El rinconero 166- tiene dos patas válidas (la 1.ª y la del cuadrante opuesto,
 * cadena de 3); elegir siempre la 1.ª colocaba el módulo en el lado ya ocupado
 * o atravesando la fila existente. Se compara contra el CENTRO del módulo
 * colocado (los orígenes de ambas patas casi coinciden en la esquina y no
 * discriminan el lado).
 */
export function composeNearestRinconeroLegSnap(
  rinconero: RinconeroUnitLike,
  moduleWidth: number,
  moduleWorldCenter: Vector3,
  moduleThickness = 0,
): Matrix4 | null {
  const candidates: Matrix4[] = []
  const firstLeg = composeMatrixModuleOnRinconeroLeg(rinconero, moduleWidth)
  if (firstLeg) candidates.push(firstLeg)
  const oppositeLeg = composeMatrixOnRinconeroByLegChain(rinconero, 3, moduleWidth)
  if (oppositeLeg) candidates.push(oppositeLeg)
  if (candidates.length === 0) return null

  const depth = moduleThickness > 0 ? moduleThickness : rinconeroPlanFondo(rinconero)
  let best: Matrix4 | null = null
  let bestDist = Number.POSITIVE_INFINITY
  for (const candidate of candidates) {
    const pos = new Vector3()
    const quat = new Quaternion()
    candidate.decompose(pos, quat, new Vector3())
    const center = new Vector3(moduleWidth / 2, 0, depth / 2).applyQuaternion(quat).add(pos)
    const dist = Math.hypot(moduleWorldCenter.x - center.x, moduleWorldCenter.z - center.z)
    if (dist < bestDist) {
      bestDist = dist
      best = candidate
    }
  }
  return best
}

/** Alias: misma lógica que resolveRinconeroSnapFaceFromApproach (cara más cercana). */
export function resolve166SnapFace(
  moduleRel: Vector3,
  rinconero: RinconeroUnitLike,
  _module?: RinconeroUnitLike,
): RinconeroSnapFace | null {
  return resolveRinconeroSnapFaceFromApproach(moduleRel, rinconero)
}

/** Origen módulo − origen rinconero (no usar posición del arrastrado si es el propio rinconero). */
export function moduleRelativeToRinconero(rinconeroPos: Vector3, modulePos: Vector3): Vector3 {
  return modulePos.clone().sub(rinconeroPos).setY(0)
}

export function rinconeroOpenFaceSnapAllowed(
  dragPos: Vector3,
  rinconero: RinconeroUnitLike,
  moduleWidth = 0,
): { allowed: boolean; face: RinconeroSnapFace | null; moduleRel: Vector3 } {
  if (!rinconero.matrix) {
    return { allowed: false, face: null, moduleRel: new Vector3() }
  }
  const rinPos = new Vector3()
  rinconero.matrix.decompose(rinPos, new Quaternion(), new Vector3())
  const moduleRel = moduleRelativeToRinconero(rinPos, dragPos)
  const face = resolveRinconeroSnapFaceFromApproach(moduleRel, rinconero, moduleWidth, dragPos)
  return { allowed: face != null, face, moduleRel }
}

export function rinconeroDraggedOpenFaceSnapAllowed(
  rinconeroWorldPos: Vector3,
  rinconero: RinconeroUnitLike,
  targetUnit: RinconeroUnitLike,
): boolean {
  if (!targetUnit.matrix) return false
  const targetPos = new Vector3()
  targetUnit.matrix.decompose(targetPos, new Quaternion(), new Vector3())
  const approach = rinconeroWorldPos.clone().sub(targetPos).setY(0)
  const face = resolveSnapFaceFor166Collision(approach, rinconero, {
    dragIsRinconero: true,
    other: targetUnit,
    moduleWorldPos: rinconeroWorldPos,
    moduleWidth: rinconeroPlanWidth(rinconero),
  })
  return face != null
}

/**
 * Dos caras exteriores (como KBR): lateral (fila) o pata (L con giro).
 * Elige la cara válida según acercamiento dominante (sin fallback por distancia).
 */
export function resolveRinconeroSnapFaceFromApproach(
  moduleRelativeToRinconero: Vector3,
  rinconero: RinconeroUnitLike,
  moduleWidth = 0,
  moduleWorldPos?: Vector3,
  options?: { suppressFaceLog?: boolean },
): RinconeroSnapFace | null {
  const suppressLog = Boolean(options?.suppressFaceLog)
  if (!rinconero.matrix) return null
  if (isApproachingRinconeroInterior(moduleRelativeToRinconero, rinconero)) {
    if (!suppressLog) {
      logRinconeroSnap('approach', 'rechazado-esquina-interior-L', {
        catalog: rinconero.catalogID,
        approach: formatApproachFromRel(moduleRelativeToRinconero, rinconero),
      })
    }
    return null
  }
  if (isApproachingCushionedRinconeroFace(moduleRelativeToRinconero, rinconero)) {
    if (!suppressLog) {
      logRinconeroSnap('approach', 'rechazado-lado-con-respaldo', {
        catalog: rinconero.catalogID,
        approach: formatApproachFromRel(moduleRelativeToRinconero, rinconero),
      })
    }
    return null
  }

  const width =
    moduleWidth > 0 ? moduleWidth : rinconeroPlanWidth(rinconero) || Number(rinconero.width || 0)
  const rinPos = new Vector3()
  rinconero.matrix.decompose(rinPos, new Quaternion(), new Vector3())
  const mPos = moduleWorldPos ?? rinPos.clone().add(moduleRelativeToRinconero)
  const distances = computeRinconeroSnapFaceDistances(rinconero, width, mPos)
  const canLateral = isExteriorLateralApproach(moduleRelativeToRinconero, rinconero)
  const canLeg = isExteriorLegApproach(moduleRelativeToRinconero, rinconero)

  if (!canLateral && !canLeg) {
    if (!suppressLog) {
      logRinconeroSnap('face', 'sin-cara-valida-lateral-ni-pata', {
        catalog: rinconero.catalogID,
        approach: formatApproachFromRel(moduleRelativeToRinconero, rinconero),
        modulePos: formatDragPos3(mPos),
        distLateral: Number.isFinite(distances.distLateral)
          ? Number(distances.distLateral.toFixed(0))
          : null,
        distLeg: Number.isFinite(distances.distLeg) ? Number(distances.distLeg.toFixed(0)) : null,
      })
    }
    return null
  }

  const distLateral = distances.distLateral
  const distLeg = distances.distLeg
  const quat = new Quaternion()
  rinconero.matrix.decompose(new Vector3(), quat, new Vector3())
  const { xProj, zProj } = rinconeroApproachProjections(moduleRelativeToRinconero, quat)
  const absX = Math.abs(xProj)
  const absZ = Math.abs(zProj)

  let face: RinconeroSnapFace
  if (canLateral && canLeg) {
    if (absX >= absZ * 1.15) face = 'lateral'
    else if (absZ >= absX * 1.15) face = 'leg'
    else face = distLeg <= distLateral ? 'leg' : 'lateral'
  } else {
    face = canLeg ? 'leg' : 'lateral'
  }

  const faceLogKey = `${String(rinconero.catalogID)}:${face}:${Number(distLateral.toFixed(0))}:${Number(mPos.x.toFixed(0))}:${Number(mPos.z.toFixed(0))}`
  if (!suppressLog && faceLogKey !== lastFaceLogKey) {
    lastFaceLogKey = faceLogKey
    logRinconeroSnap('face', 'cara-elegida-por-acercamiento', {
      catalog: rinconero.catalogID,
      face,
      distLateral: Number.isFinite(distLateral) ? Number(distLateral.toFixed(0)) : null,
      distLeg: Number.isFinite(distLeg) ? Number(distLeg.toFixed(0)) : null,
      canLateral,
      canLeg,
      approach: formatApproachFromRel(moduleRelativeToRinconero, rinconero),
    })
  }
  return face
}

/** @deprecated Usar resolveRinconeroSnapFaceFromApproach */
export function resolveClosestRinconeroSnapFace(
  moduleRelativeToRinconero: Vector3,
  rinconero: RinconeroUnitLike,
): RinconeroSnapFace | null {
  return resolveRinconeroSnapFaceFromApproach(moduleRelativeToRinconero, rinconero)
}

/**
 * Cara de snap según quién se arrastra.
 * Rinconero: solo lateral (fila) o pata (L). Sin fallback en caras no válidas.
 */
export function resolveSnapFaceFor166Collision(
  moduleRelativeToRinconero: Vector3,
  rinconero: RinconeroUnitLike,
  options?: {
    dragIsRinconero?: boolean
    other?: RinconeroUnitLike
    nearestWhenAmbiguous?: boolean
    moduleWidth?: number
    moduleWorldPos?: Vector3
  },
): RinconeroSnapFace | null {
  const dragIsRinconero = Boolean(options?.dragIsRinconero)
  const other = options?.other
  if (dragIsRinconero && other?.matrix && !isRinconeroCatalog(other.catalogID)) {
    const rinconeroWorldPos =
      options?.moduleWorldPos ??
      (() => {
        const unitPos = new Vector3()
        other.matrix!.decompose(unitPos, new Quaternion(), new Vector3())
        return unitPos.clone().add(moduleRelativeToRinconero)
      })()
    const estimate = estimateRinconeroDraggedToUnitSnap(other, rinconero, rinconeroWorldPos)
    logRinconeroSnap('face', 'rinconero-arrastrado-hacia-unidad', {
      rinconeroCatalog: rinconero.catalogID,
      otherCatalog: other.catalogID,
      face: estimate.face,
      minDist: Number(estimate.minDist.toFixed(0)),
      distLateral: Number(estimate.distLateral.toFixed(0)),
      distLeg: Number(estimate.distLeg.toFixed(0)),
    })
    return estimate.face
  }
  return resolveRinconeroSnapFaceFromApproach(
    moduleRelativeToRinconero,
    rinconero,
    options?.moduleWidth ?? 0,
    options?.moduleWorldPos,
  )
}

/** Ambas posiciones laterales (fila) posibles de un módulo pegado al rinconero. */
export function computeBothModuleLateralSnapPositions(
  rinconero: RinconeroUnitLike,
  moduleWidth: number,
): { after: Vector3; before: Vector3 } | null {
  if (!rinconero.matrix) return null
  const pos = new Vector3()
  const quat = new Quaternion()
  rinconero.matrix.decompose(pos, quat, new Vector3())
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(quat)
  const rinconeroWidth = rinconeroPlanWidth(rinconero)
  const after = pos.clone().add(xAxis.clone().multiplyScalar(rinconeroWidth))
  alignRinconeroRowSnapDepth(after, rinconero)
  const before = pos.clone().add(xAxis.clone().multiplyScalar(-moduleWidth))
  alignRinconeroRowSnapDepth(before, rinconero)
  return { after, before }
}

/** Posición del módulo recto pegado al lateral del rinconero (cara fila). */
export function computeModuleLateralSnapPosition(
  rinconero: RinconeroUnitLike,
  moduleWidth: number,
  moduleRelativeToRinconero: Vector3,
  moduleWorldPos?: Vector3,
): Vector3 | null {
  const candidates = computeBothModuleLateralSnapPositions(rinconero, moduleWidth)
  if (!candidates) return null
  if (!rinconero.matrix) return null
  if (isApproachingCushionedRinconeroFace(moduleRelativeToRinconero, rinconero)) {
    return null
  }
  const validSide = rinconeroRowLateralSide(rinconero)
  return validSide === 'after' ? candidates.after : candidates.before
}

/** Rinconero arrastrado hacia módulo recto: extremo más cercano + metadata. */
export function composeClosestRinconeroOnUnitEndMatrixWithEnd(
  unit: RinconeroUnitLike,
  rinconeroWidth: number,
  rinconeroWorldPos: Vector3,
): { matrix: Matrix4; attachEnd: 'left' | 'right' } | null {
  let bestMatrix: Matrix4 | null = null
  let bestDist = Number.POSITIVE_INFINITY
  let bestEnd: 'left' | 'right' | null = null

  for (const attachEnd of ['left', 'right'] as const) {
    const matrix = composeMatrixRinconeroOnUnitEnd(
      unit,
      attachEnd,
      attachEnd === 'left' ? rinconeroWidth : 0,
    )
    if (!matrix) continue
    const pos = new Vector3()
    matrix.decompose(pos, new Quaternion(), new Vector3())
    const dist = Math.hypot(rinconeroWorldPos.x - pos.x, rinconeroWorldPos.z - pos.z)
    if (dist < bestDist) {
      bestDist = dist
      bestMatrix = matrix
      bestEnd = attachEnd
    }
  }

  if (!bestMatrix || !bestEnd) return null
  return { matrix: bestMatrix, attachEnd: bestEnd }
}

/** Rinconero arrastrado hacia módulo recto: el extremo lateral más cercano. */
export function composeClosestRinconeroOnUnitEndMatrix(
  unit: RinconeroUnitLike,
  rinconeroWidth: number,
  rinconeroWorldPos: Vector3,
): Matrix4 | null {
  return (
    composeClosestRinconeroOnUnitEndMatrixWithEnd(unit, rinconeroWidth, rinconeroWorldPos)
      ?.matrix ?? null
  )
}

/**
 * Gira 180° en Y si el rinconero se ancla en el extremo opuesto a su mano (166 izq/der).
 * Solo en arrastre final — no usar para medir distancia lateral vs pata.
 */
export function mirrorRinconeroSnapForOppositeRowEnd(
  snapMatrix: Matrix4,
  unit: RinconeroUnitLike,
  attachEnd: 'left' | 'right',
): Matrix4 {
  const pos = new Vector3()
  const quat = new Quaternion()
  const scale = new Vector3()
  snapMatrix.decompose(pos, quat, scale)
  if (!unit.matrix) return snapMatrix

  const unitPos = new Vector3()
  const unitQuat = new Quaternion()
  unit.matrix.decompose(unitPos, unitQuat, new Vector3())
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(unitQuat)
  const unitWidth = rinconeroPlanWidth(unit) || Number(unit.width || 0)
  const pivot =
    attachEnd === 'right'
      ? unitPos.clone().add(xAxis.clone().multiplyScalar(unitWidth))
      : unitPos.clone()
  alignRinconeroRowSnapDepth(pivot, unit)

  const flipQuat = new Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), Math.PI)
  const newQuat = quat.clone().multiply(flipQuat)
  const offset = pos.clone().sub(pivot)
  offset.applyQuaternion(flipQuat)
  const newPos = pivot.clone().add(offset)
  return new Matrix4().compose(newPos, newQuat, scale)
}

/** Alcance base (mm) al punto objetivo de snap — no centro a centro. */
export const RINCONERO_SNAP_TARGET_REACH_MM = 900

/** Alcance dinámico: base + fracción del ancho (tras separar por la fila hace falta más radio). */
export function dynamicRinconeroSnapReachMm(
  rinconero?: RinconeroUnitLike,
  moduleWidth = 0,
): number {
  const rw = rinconeroPlanWidth(rinconero)
  const mw = moduleWidth > 0 ? moduleWidth : 0
  return RINCONERO_SNAP_TARGET_REACH_MM + Math.max(rw, mw) * 0.85
}

type RinconeroSnapFaceDistances = {
  distLateral: number
  distLeg: number
  distLateralAfter: number
  distLateralBefore: number
}

function computeRinconeroSnapFaceDistances(
  rinconero: RinconeroUnitLike,
  moduleWidth: number,
  moduleWorldPos: Vector3,
): RinconeroSnapFaceDistances {
  let distLateralAfter = Number.POSITIVE_INFINITY
  let distLateralBefore = Number.POSITIVE_INFINITY
  let distLeg = Number.POSITIVE_INFINITY

  const lateral = computeBothModuleLateralSnapPositions(rinconero, moduleWidth)
  if (lateral) {
    distLateralAfter = Math.hypot(
      moduleWorldPos.x - lateral.after.x,
      moduleWorldPos.z - lateral.after.z,
    )
    distLateralBefore = Math.hypot(
      moduleWorldPos.x - lateral.before.x,
      moduleWorldPos.z - lateral.before.z,
    )
  }
  const legMatrix = composeMatrixModuleOnRinconeroLeg(rinconero, moduleWidth)
  if (legMatrix) {
    const legPos = new Vector3()
    legMatrix.decompose(legPos, new Quaternion(), new Vector3())
    distLeg = Math.hypot(moduleWorldPos.x - legPos.x, moduleWorldPos.z - legPos.z)
  }

  const distLateral = Math.min(distLateralAfter, distLateralBefore)
  return { distLateral, distLeg, distLateralAfter, distLateralBefore }
}

export type RinconeroDragSnapEstimate = {
  face: RinconeroSnapFace | null
  minDist: number
  distLateral: number
  distLeg: number
}

/** Distancia al objetivo de snap: prioriza eje de fila (penaliza desfase en profundidad). */
export function rinconeroRowWeightedReachDistance(
  unit: RinconeroUnitLike,
  fromPos: Vector3,
  targetPos: Vector3,
): number {
  if (!unit.matrix) {
    return Math.hypot(fromPos.x - targetPos.x, fromPos.z - targetPos.z)
  }
  const unitQuat = new Quaternion()
  unit.matrix.decompose(new Vector3(), unitQuat, new Vector3())
  const delta = fromPos.clone().sub(targetPos).setY(0)
  const { xProj, zProj } = rinconeroApproachProjections(delta, unitQuat)
  return Math.hypot(Math.abs(xProj), Math.abs(zProj) * 0.12)
}

/** Extremo de fila al arrastrar rinconero: mismo criterio que directionMultiplier en collideItems (signo de xProj). */
export function resolveRinconeroDragRowAttachEnd(
  unit: RinconeroUnitLike,
  rinWorldPos: Vector3,
  _rinconeroWidth: number,
): 'left' | 'right' {
  if (!unit.matrix) return 'right'
  const unitPos = new Vector3()
  const unitQuat = new Quaternion()
  unit.matrix.decompose(unitPos, unitQuat, new Vector3())
  const approachDelta = rinWorldPos.clone().sub(unitPos).setY(0)
  const { xProj } = rinconeroApproachProjections(approachDelta, unitQuat)
  return xProj >= 0 ? 'right' : 'left'
}

/**
 * Rotación en extremo opuesto al catálogo: ±90° local sobre el módulo (como la pata en L),
 * con signo según orientación del módulo para obtener 270°/-90° en escena.
 */
export function rinconeroOppositeRowEndQuaternion(unitQuat: Quaternion, izq: boolean): Quaternion {
  const moduleEuler = new Euler().setFromQuaternion(unitQuat, 'YXZ')
  let moduleDeg = (moduleEuler.y * 180) / Math.PI
  moduleDeg = ((moduleDeg % 360) + 360) % 360
  const moduleFacesBack = moduleDeg > 90 && moduleDeg < 270
  const adjustmentRad = izq
    ? moduleFacesBack
      ? -Math.PI / 2
      : Math.PI / 2
    : moduleFacesBack
      ? Math.PI / 2
      : -Math.PI / 2
  const adjustment = new Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), adjustmentRad)
  return unitQuat.clone().multiply(adjustment)
}

/** Distancia del rinconero arrastrado a los puntos objetivo lateral/pata sobre un módulo. */
export function estimateRinconeroDraggedToUnitSnap(
  unit: RinconeroUnitLike,
  rinconero: RinconeroUnitLike,
  rinconeroWorldPos: Vector3,
  currentRinconeroQuat?: Quaternion,
): RinconeroDragSnapEstimate {
  const hand = rinconeroHandednessFromItem(rinconero)
  let distLateral = Number.POSITIVE_INFINITY
  let distLeg = Number.POSITIVE_INFINITY

  const lateralMatrix = composeRinconeroDragRowEndSnap(
    unit,
    rinconero,
    rinconeroWorldPos,
    currentRinconeroQuat,
  )
  if (lateralMatrix) {
    const lateralPos = new Vector3()
    lateralMatrix.decompose(lateralPos, new Quaternion(), new Vector3())
    distLateral = rinconeroRowWeightedReachDistance(unit, rinconeroWorldPos, lateralPos)
  }

  const legMatrix = composeMatrixRinconeroOnModuleLeg(unit, rinconero.catalogID, hand)
  if (legMatrix) {
    const legPos = new Vector3()
    legMatrix.decompose(legPos, new Quaternion(), new Vector3())
    distLeg = Math.hypot(rinconeroWorldPos.x - legPos.x, rinconeroWorldPos.z - legPos.z)
  }

  let face: RinconeroSnapFace | null = null
  if (Number.isFinite(distLateral) || Number.isFinite(distLeg)) {
    if (unit.matrix) {
      const unitPos = new Vector3()
      const unitQuat = new Quaternion()
      unit.matrix.decompose(unitPos, unitQuat, new Vector3())
      const approachDelta = rinconeroWorldPos.clone().sub(unitPos).setY(0)
      const { xProj, zProj } = rinconeroApproachProjections(approachDelta, unitQuat)
      if (Math.abs(xProj) >= Math.abs(zProj) * 0.42) {
        face = 'lateral'
      } else {
        face = distLeg <= distLateral ? 'leg' : 'lateral'
      }
    } else {
      face = distLeg <= distLateral ? 'leg' : 'lateral'
    }
  }

  const minDist =
    face === 'lateral' ? distLateral : face === 'leg' ? distLeg : Math.min(distLateral, distLeg)

  return { face, minDist, distLateral, distLeg }
}

/** Distancia del módulo arrastrado al punto objetivo de snap sobre rinconero. */
export function estimateModuleToRinconeroSnapDistance(
  rinconero: RinconeroUnitLike,
  moduleWidth: number,
  moduleWorldPos: Vector3,
): { face: RinconeroSnapFace | null; minDist: number } {
  const rinPos = new Vector3()
  rinconero.matrix?.decompose(rinPos, new Quaternion(), new Vector3())
  const rel = moduleRelativeToRinconero(rinPos, moduleWorldPos)
  const face = resolveRinconeroSnapFaceFromApproach(rel, rinconero, moduleWidth, moduleWorldPos)
  if (!face) return { face: null, minDist: Number.POSITIVE_INFINITY }
  const minDist = distanceToRinconeroSnapTarget(rinconero, moduleWidth, rel, moduleWorldPos, face)
  return { face, minDist }
}

/** Posición mundial del punto de snap (lateral o pata), no el centro del rinconero. */
export function getRinconeroSnapTargetWorldPos(
  rinconero: RinconeroUnitLike,
  moduleWidth: number,
  moduleRelativeToRinconero: Vector3,
  moduleWorldPos: Vector3,
  face: RinconeroSnapFace,
): Vector3 | null {
  if (face === 'leg') {
    const legMatrix = composeMatrixModuleOnRinconeroLeg(rinconero, moduleWidth)
    if (!legMatrix) return null
    const legPos = new Vector3()
    legMatrix.decompose(legPos, new Quaternion(), new Vector3())
    return legPos
  }
  return computeModuleLateralSnapPosition(
    rinconero,
    moduleWidth,
    moduleRelativeToRinconero,
    moduleWorldPos,
  )
}

/**
 * ¿El arrastre se aleja del punto de snap real (lateral/pata)?
 * No usar el centro del rinconero: en fila (xProj grande) eso libera el lock al volver.
 */
export function isDragMovingAwayFromRinconeroSnapTarget(
  frameMove: Vector3,
  modulePos: Vector3,
  rinconero: RinconeroUnitLike,
  moduleWidth: number,
): boolean {
  if (!rinconero.matrix) return false
  const move = frameMove.clone().setY(0)
  if (move.lengthSq() < 16) return false

  const rinPos = new Vector3()
  rinconero.matrix.decompose(rinPos, new Quaternion(), new Vector3())
  const moduleRel = moduleRelativeToRinconero(rinPos, modulePos)
  const face = resolveRinconeroSnapFaceFromApproach(moduleRel, rinconero, moduleWidth, modulePos)
  if (!face) return false

  const snapTarget = getRinconeroSnapTargetWorldPos(
    rinconero,
    moduleWidth,
    moduleRel,
    modulePos,
    face,
  )
  if (!snapTarget) return false

  const towardSnap = snapTarget.clone().sub(modulePos).setY(0)
  if (towardSnap.lengthSq() < 1) return false
  return move.dot(towardSnap) < -25
}

/** Distancia relevante para imán 166- (al punto objetivo, no centro a centro). */
export function collisionDistanceFor166SnapPair(
  dragItem: RinconeroUnitLike & { matrix?: RinconeroUnitLike['matrix'] },
  otherItem: RinconeroUnitLike & { matrix?: RinconeroUnitLike['matrix']; width?: number },
  dragPos: Vector3,
  draggedWidth = 0,
): number {
  const dragIsRinconero = isRinconeroCatalog(dragItem.catalogID)
  const otherIsRinconero = isRinconeroCatalog(otherItem.catalogID)
  if (dragIsRinconero && !otherIsRinconero && otherItem.matrix) {
    const dragQuat = new Quaternion()
    dragItem.matrix!.decompose(new Vector3(), dragQuat, new Vector3())
    const estimate = estimateRinconeroDraggedToUnitSnap(otherItem, dragItem, dragPos, dragQuat)
    return estimate.minDist
  }
  if (!dragIsRinconero && otherIsRinconero && otherItem.matrix) {
    const width =
      draggedWidth > 0 ? draggedWidth : rinconeroPlanWidth(dragItem) || Number(dragItem.width || 0)
    return estimateModuleToRinconeroSnapDistance(otherItem, width, dragPos).minDist
  }
  if (!dragIsRinconero && !otherIsRinconero) {
    const lateralDist = straightModuleLateralSnapDistance(
      dragItem,
      otherItem,
      dragPos,
      draggedWidth,
    )
    if (lateralDist != null) return lateralDist
  }
  const otherPos = new Vector3()
  otherItem.matrix?.decompose(otherPos, new Quaternion(), new Vector3())
  return Math.hypot(dragPos.x - otherPos.x, dragPos.z - otherPos.z)
}

/** Lado de fila al dibujar esquina desde un ancla (offset mm: + derecha, − izquierda). */
export function resolveModularCornerDrawAttachEnd(offsetMm: number): 'left' | 'right' {
  return offsetMm < 0 ? 'left' : 'right'
}

/** Lado del ancla al dibujar esquina (166 vs corner XL). */
export function resolveAttachEndForCornerDraw(
  drawingCatalog: unknown,
  offsetMm = 0,
): 'left' | 'right' {
  if (isCornerXLHandedCatalog(drawingCatalog)) {
    return resolveModularCornerDrawAttachEnd(offsetMm)
  }
  return isRinconeroDerecha(drawingCatalog) ? 'right' : 'left'
}

/**
 * Matriz al dibujar esquina modular tras un módulo recto.
 * Corner XL (181 D/I): prolonga la fila y aplica giro si la mano lo exige
 * (composeRinconeroDragRowEndSnap). 166-: extremo natural del catálogo.
 */
export function composeModularCornerOnUnitDrawMatrix(
  unit: RinconeroUnitLike,
  drawingCatalog: unknown,
  offsetMm = 0,
): Matrix4 | null {
  if (!unit.matrix) return null
  const drawnWidth =
    defaultWidthForDrawCatalog(drawingCatalog) || rinconeroPlanWidth({ catalogID: drawingCatalog })

  if (isCornerXLHandedCatalog(drawingCatalog)) {
    const unitPos = new Vector3()
    const unitQuat = new Quaternion()
    unit.matrix.decompose(unitPos, unitQuat, new Vector3())
    const unitX = new Vector3(1, 0, 0).applyQuaternion(unitQuat)
    const unitWidth = rinconeroPlanWidth(unit) || Number(unit.width || 0)
    const attachEnd = resolveModularCornerDrawAttachEnd(offsetMm)
    const probeAlongX = attachEnd === 'right' ? unitWidth + 300 : -300
    const probePos = unitPos.clone().add(unitX.multiplyScalar(probeAlongX))
    const rinconero: RinconeroUnitLike = {
      catalogID: drawingCatalog,
      width: drawnWidth,
      thickness: unit.thickness,
    }
    return composeRinconeroDragRowEndSnap(unit, rinconero, probePos)
  }

  const hand = rinconeroHandednessFromItem({ catalogID: drawingCatalog })
  const attachEnd = isRinconeroIzquierda(drawingCatalog, hand) ? 'left' : 'right'
  return composeMatrixRinconeroOnUnitEnd(unit, attachEnd, attachEnd === 'left' ? drawnWidth : 0)
}

/** Snap final de rinconero arrastrado al extremo de fila (con giro si la mano no coincide). */
export function composeRinconeroDragRowEndSnap(
  unit: RinconeroUnitLike,
  rinconero: RinconeroUnitLike,
  rinconeroWorldPos: Vector3,
  currentRinconeroQuat?: Quaternion,
): Matrix4 | null {
  if (!unit.matrix) return null
  const rinconeroWidth = rinconeroPlanWidth(rinconero)
  const unitPos = new Vector3()
  const unitQuat = new Quaternion()
  const scale = new Vector3()
  unit.matrix.decompose(unitPos, unitQuat, scale)
  const attachEnd = resolveRinconeroDragRowAttachEnd(unit, rinconeroWorldPos, rinconeroWidth)

  const baseMatrix = composeMatrixRinconeroOnUnitEnd(
    unit,
    attachEnd,
    attachEnd === 'left' ? rinconeroWidth : 0,
  )
  if (!baseMatrix) return null

  const hand = rinconeroHandednessFromItem(rinconero)
  const naturalEnd = isRinconeroIzquierda(rinconero.catalogID, hand) ? 'left' : 'right'
  const needsFlip = attachEnd !== naturalEnd
  const izq = isRinconeroIzquierda(rinconero.catalogID, hand)

  // Extremo natural: misma rotación que el módulo (como getRelativeMatrix / items rectos).
  // Extremo opuesto: ±90° local (misma familia que composeMatrixRinconeroOnModuleLeg).
  const finalQuat = needsFlip ? rinconeroOppositeRowEndQuaternion(unitQuat, izq) : unitQuat.clone()

  let snapPos: Vector3
  if (!needsFlip) {
    snapPos = new Vector3()
    baseMatrix.decompose(snapPos, new Quaternion(), new Vector3())
  } else {
    const unitWidth = rinconeroPlanWidth(unit) || Number(unit.width || 0)
    const unitXAxis = new Vector3(1, 0, 0).applyQuaternion(unitQuat)
    const unitZAxis = new Vector3(0, 0, 1).applyQuaternion(unitQuat)
    const zSign = rinconeroFondoAxisSign(rinconero.catalogID, hand)

    const corner = unitPos.clone()
    alignRinconeroRowSnapDepth(corner, unit)
    if (attachEnd === 'right') {
      corner.add(unitXAxis.multiplyScalar(unitWidth))
      alignRinconeroRowSnapDepth(corner, unit)
    }

    // Esquina exterior de la L en `corner`; origen interior = esquina − offset local rotado.
    const extOffset = rinconeroExteriorPivotLocalOffset({
      catalogID: rinconero.catalogID,
      width: rinconeroWidth,
      thickness: rinconeroPlanFondo(rinconero),
      hand: (rinconero as { hand?: unknown }).hand,
      mano: (rinconero as { mano?: unknown }).mano,
    })
    snapPos = corner.clone().sub(extOffset.clone().applyQuaternion(finalQuat))
    snapPos.add(
      unitZAxis.multiplyScalar(
        zSign * RINCONERO_LATERAL_FLIPPED_ROW_ALIGN_OFFSET_MM * (attachEnd === 'left' ? 1 : -1),
      ),
    )
  }

  const matrix = new Matrix4().compose(snapPos, finalQuat, scale)

  return matrix
}

/**
 * Rinconero arrastrado hacia la pata de un módulo recto (inverso de composeMatrixModuleOnRinconeroLeg).
 */
export function composeMatrixRinconeroOnModuleLeg(
  module: RinconeroUnitLike,
  rinconeroCatalog?: unknown,
  rinconeroHand?: RinconeroHandednessContext,
): Matrix4 | null {
  if (!module.matrix) return null
  const position = new Vector3()
  const quaternion = new Quaternion()
  const scale = new Vector3()
  module.matrix.decompose(position, quaternion, scale)
  const moduleWidth = rinconeroPlanWidth(module)
  const moduleFondo = rinconeroPlanFondo(module)
  const fondoSign = rinconeroFondoAxisSign(module.catalogID, rinconeroHandednessFromItem(module))
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(quaternion)
  const zAxis = new Vector3(0, 0, 1).applyQuaternion(quaternion)
  const izq = isRinconeroIzquierda(rinconeroCatalog, rinconeroHand)
  const rotationAdjustment = new Quaternion().setFromAxisAngle(
    new Vector3(0, 1, 0),
    izq ? -Math.PI / 2 : Math.PI / 2,
  )
  const newQuaternion = quaternion.clone().multiply(rotationAdjustment)
  const newPosition = position
    .clone()
    .add(xAxis.multiplyScalar(moduleWidth))
    .add(zAxis.multiplyScalar(fondoSign * moduleFondo))
  applyRinconeroLegSnapFineOffset(newPosition, quaternion, rinconeroCatalog, rinconeroHand)
  return new Matrix4().compose(newPosition, newQuaternion, scale)
}

/** Profundidad de la pata en L: serie 166- solo usa fondo (sin regleta). */
export function rinconeroLegDepthOffset(rinconero?: RinconeroUnitLike): number {
  if (!rinconero) return 0
  const fondoMorph = rinconero.morphs?.find((m) => m.name === 'Fondo' || m.name === 'thickness')
  const morphDepth =
    fondoMorph != null
      ? Number(
          (fondoMorph as { default?: number; _default?: number }).default ??
            (fondoMorph as { _default?: number })._default,
        )
      : NaN
  const depth =
    Number.isFinite(morphDepth) && morphDepth > 0 ? morphDepth : Number(rinconero.thickness || 0)
  return depth
}

/**
 * Colocar rinconero 166- al final de la fila: sigue en línea, misma rotación que la unidad.
 * La L perpendicular solo aplica al dibujar el módulo siguiente (composeMatrixModuleOnRinconeroLeg).
 */
export function composeMatrixRinconeroOnUnitEnd(
  unit: RinconeroUnitLike,
  attachEnd: 'right' | 'left' = 'right',
  attachedWidth = 0,
): Matrix4 | null {
  if (!unit.matrix) return null
  const position = new Vector3()
  const quaternion = new Quaternion()
  const scale = new Vector3()
  unit.matrix.decompose(position, quaternion, scale)
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(quaternion)
  const unitWidth = rinconeroPlanWidth(unit) || Number(unit.width || 0)

  if (attachEnd === 'right') {
    const newPosition = position.clone().add(xAxis.multiplyScalar(unitWidth))
    alignRinconeroRowSnapDepth(newPosition, unit)
    return new Matrix4().compose(newPosition, quaternion.clone(), scale)
  }

  const newPosition = position.clone().add(xAxis.multiplyScalar(-attachedWidth))
  alignRinconeroRowSnapDepth(newPosition, unit)
  return new Matrix4().compose(newPosition, quaternion.clone(), scale)
}

/**
 * Módulo recto en el lateral del rinconero (cara fila, sin girar).
 * Misma rotación que el rinconero; respaldo alineado en el plano de fondo.
 */
export function composeMatrixModuleOnRinconeroLateral(
  rinconero: RinconeroUnitLike,
  moduleWidth: number,
  attachOnNegativeLocalX = true,
): Matrix4 | null {
  if (!rinconero.matrix) return null
  const position = new Vector3()
  const quaternion = new Quaternion()
  const scale = new Vector3()
  rinconero.matrix.decompose(position, quaternion, scale)
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(quaternion)
  const approach = xAxis.clone().multiplyScalar(attachOnNegativeLocalX ? -1 : 1)
  const lateralPos = computeModuleLateralSnapPosition(rinconero, moduleWidth, approach)
  if (!lateralPos) return null
  return new Matrix4().compose(lateralPos, quaternion, scale)
}

/**
 * Módulo recto en la pata del rinconero (cara fondo, perpendicular en L):
 * esquina = origen + ejeX×ancho; respaldo alineado; giro ±90°.
 */
export function composeMatrixModuleOnRinconeroLeg(
  rinconero: RinconeroUnitLike,
  moduleWidth = 0,
): Matrix4 | null {
  if (!rinconero.matrix) return null
  const position = new Vector3()
  const quaternion = new Quaternion()
  const scale = new Vector3()
  rinconero.matrix.decompose(position, quaternion, scale)
  const hand = rinconeroHandednessFromItem(rinconero)
  const izq = isRinconeroIzquierda(rinconero.catalogID, hand)
  const rinconeroWidth = rinconeroPlanWidth(rinconero)

  const xAxis = new Vector3(1, 0, 0).applyQuaternion(quaternion)
  const rotationAdjustment = new Quaternion().setFromAxisAngle(
    new Vector3(0, 1, 0),
    izq ? Math.PI / 2 : -Math.PI / 2,
  )
  const newQuaternion = quaternion.clone().multiply(rotationAdjustment)

  // Esquina exterior del rinconero: +ancho (der) / −ancho (izq), igual que rinconeroExteriorPivotLocalOffset.
  const cornerSign = izq ? -1 : 1
  const newPosition = position.clone().add(xAxis.multiplyScalar(cornerSign * rinconeroWidth))
  alignModuleOnRinconeroLegSnap(newPosition, rinconero)

  return new Matrix4().compose(newPosition, newQuaternion, scale)
}

/**
 * Arrastre: el rinconero solo puede pegarse al extremo interior de la fila.
 */
export function isApproachingUnitInteriorEndForRinconero(
  approachDelta: Vector3,
  unitQuat: Quaternion,
  end: 'right' | 'left',
): boolean {
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(unitQuat)
  const zAxis = new Vector3(0, 0, 1).applyQuaternion(unitQuat)
  const xProj = approachDelta.dot(xAxis)
  const zProj = approachDelta.dot(zAxis)
  if (Math.abs(zProj) > Math.abs(xProj) * 0.85) return false
  return end === 'right' ? xProj > 0 : xProj < 0
}

/**
 * Durante el arrastre: solo re-snapear si el usuario se acerca al otro mueble,
 * no al alejarse (evita quedar “bloqueado” con OBB siempre intersectando).
 */
export function isDragMovingTowardOtherItem(
  frameDelta: Vector3,
  draggedPos: Vector3,
  otherPos: Vector3,
): boolean {
  const move = frameDelta.clone().setY(0)
  if (move.lengthSq() < 1) return true
  const toward = otherPos.clone().sub(draggedPos).setY(0)
  if (toward.lengthSq() < 1) return false
  return move.dot(toward) > 0
}

/** Proyecciones del acercamiento en ejes locales del rinconero (ancho X, fondo Z). */
export function rinconeroApproachProjections(
  approachDelta: Vector3,
  rinconeroQuat: Quaternion,
): { xProj: number; zProj: number } {
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(rinconeroQuat)
  const zAxis = new Vector3(0, 0, 1).applyQuaternion(rinconeroQuat)
  return {
    xProj: approachDelta.dot(xAxis),
    zProj: approachDelta.dot(zAxis),
  }
}

/** @deprecated Usar resolveClosestRinconeroSnapFace === 'lateral' */
export function isApproachingRinconeroLateralRowFace(
  approachDelta: Vector3,
  rinconeroQuat: Quaternion,
): boolean {
  const { xProj, zProj } = rinconeroApproachProjections(approachDelta, rinconeroQuat)
  return Math.abs(xProj) >= Math.abs(zProj)
}

/** @deprecated Usar resolveClosestRinconeroSnapFace === 'leg' */
export function isApproachingRinconeroLegInteriorFace(
  approachDelta: Vector3,
  rinconero: RinconeroUnitLike,
): boolean {
  return resolveClosestRinconeroSnapFace(approachDelta, rinconero) === 'leg'
}

/**
 * Snap lateral en fila: misma profundidad (Z local) que el origen del ancla.
 * Compartido por dibujo, arrastre y collideItems (cara fila del rinconero).
 */
export function alignRinconeroRowSnapDepth(position: Vector3, reference: RinconeroUnitLike): void {
  if (!reference.matrix) return
  const refPos = new Vector3()
  const refQuat = new Quaternion()
  reference.matrix.decompose(refPos, refQuat, new Vector3())
  const zAxis = new Vector3(0, 0, 1).applyQuaternion(refQuat)
  const delta = position.clone().sub(refPos)
  const alongZ = zAxis.dot(delta)
  position.copy(refPos).add(delta.sub(zAxis.multiplyScalar(alongZ)))
}

/** @deprecated Usar alignRinconeroRowSnapDepth */
export function alignLateralDepthToReferenceOrigin(
  position: Vector3,
  reference: RinconeroUnitLike,
): void {
  alignRinconeroRowSnapDepth(position, reference)
}

/** Ajuste fino (mm) en profundidad (eje Z local) — extremo opuesto al catálogo en fila invertida. */
export const RINCONERO_LATERAL_FLIPPED_ROW_ALIGN_OFFSET_MM = 30

/** Ajuste fino (mm) en eje Z local del ancla tras alinear al plano de respaldo (cara pata). */
export const RINCONERO_LEG_SIDE_LOCAL_DEPTH_OFFSET_MM = 30

/** @deprecated Usar RINCONERO_LEG_SIDE_LOCAL_DEPTH_OFFSET_MM */
export const RINCONERO_LEG_SIDE_WORLD_X_OFFSET_MM = RINCONERO_LEG_SIDE_LOCAL_DEPTH_OFFSET_MM

/**
 * Ajuste fino en eje X local del ancla (invariante a rotación mundial).
 * Sustituye el antiguo `position.x -= 30` del snap en pata.
 */
export function applyRinconeroLegSnapFineOffset(
  position: Vector3,
  anchorQuat: Quaternion,
  catalogID?: unknown,
  hand?: RinconeroHandednessContext,
  offsetMm = RINCONERO_LEG_SIDE_LOCAL_DEPTH_OFFSET_MM,
): void {
  if (offsetMm === 0) return
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(anchorQuat)
  const sign =
    catalogID != null && isRinconeroCatalog(catalogID)
      ? isRinconeroIzquierda(catalogID, hand)
        ? -1
        : 1
      : 1
  position.add(xAxis.multiplyScalar(sign * offsetMm))
}

/**
 * @deprecated Usar applyRinconeroLegSnapFineOffset
 */
export function applyRinconeroSnapLocalDepthOffset(
  position: Vector3,
  anchorQuat: Quaternion,
  depthSign: number,
  offsetMm = RINCONERO_LEG_SIDE_LOCAL_DEPTH_OFFSET_MM,
): void {
  if (offsetMm === 0 || depthSign === 0) return
  const zAxis = new Vector3(0, 0, 1).applyQuaternion(anchorQuat)
  position.add(zAxis.multiplyScalar(depthSign * offsetMm))
}

/** @deprecated Usar applyRinconeroLegSnapFineOffset */
export function applyRinconeroLegSideWorldZOffset(position: Vector3): void {
  applyRinconeroLegSnapFineOffset(position, new Quaternion())
}

/**
 * Módulo recto en pata del rinconero: alinear al plano de respaldo + ajuste fino en X local.
 */
export function alignModuleOnRinconeroLegSnap(
  position: Vector3,
  rinconero: RinconeroUnitLike,
): void {
  const backPoint = rinconeroBackPlanePoint(rinconero)
  if (!backPoint || !rinconero.matrix) return
  const rinconeroQuat = new Quaternion()
  rinconero.matrix.decompose(new Vector3(), rinconeroQuat, new Vector3())
  const zAxis = new Vector3(0, 0, 1).applyQuaternion(rinconeroQuat)
  const delta = position.clone().sub(backPoint)
  const alongZ = zAxis.dot(delta)
  position.copy(backPoint).add(delta.sub(zAxis.multiplyScalar(alongZ)))
  applyRinconeroLegSnapFineOffset(
    position,
    rinconeroQuat,
    rinconero.catalogID,
    rinconeroHandednessFromItem(rinconero),
  )
}

/**
 * Profundidad unificada para snap en pata (L): proyecta sobre el plano de respaldo del
 * ancla y aplica el ajuste fino en eje X local. Usado al colocar módulo en pata del rinconero.
 */
export function alignLegSnapDepthToAnchorBackPlane(
  position: Vector3,
  anchor: RinconeroUnitLike,
  attachedCatalog?: unknown,
  attachedHand?: RinconeroHandednessContext,
): void {
  if (isRinconeroCatalog(anchor.catalogID)) {
    alignModuleOnRinconeroLegSnap(position, anchor)
    return
  }
  const backPoint = rinconeroBackPlanePoint(anchor)
  if (!backPoint || !anchor.matrix) return
  const anchorQuat = new Quaternion()
  anchor.matrix.decompose(new Vector3(), anchorQuat, new Vector3())
  const zAxis = new Vector3(0, 0, 1).applyQuaternion(anchorQuat)
  const delta = position.clone().sub(backPoint)
  const alongZ = zAxis.dot(delta)
  position.copy(backPoint).add(delta.sub(zAxis.multiplyScalar(alongZ)))
  applyRinconeroLegSnapFineOffset(position, anchorQuat, attachedCatalog, attachedHand)
}

/** Tras snap en pata (L): alias de alignModuleOnRinconeroLegSnap para el rinconero ancla. */
export function alignPositionToRinconeroBackRow(
  position: Vector3,
  rinconero: RinconeroUnitLike,
): void {
  alignModuleOnRinconeroLegSnap(position, rinconero)
}

type SceneUnit = {
  id?: AnyID
  type?: string
  catalogID?: unknown
  width?: number
  matrix?: { decompose: (p: Vector3, q: Quaternion, s: Vector3) => void }
  firstParentID?: AnyID
  childs?: Set<string>
}

/** Devuelve la unidad ancla para dibujo encadenado (puff / modular). */
export function resolveAnchorUnitForRinconeroDraw(
  items: Map<string, SceneUnit>,
  anchorId: AnyID,
): { unitId: AnyID; unit: SceneUnit } | null {
  const anchor = items.get(anchorId as string)
  if (!anchor || anchor.type !== 'linkedItem') {
    logRinconeroDraw('resolveAnchor:no-encontrado', { anchorId: String(anchorId) })
    return null
  }
  logRinconeroDraw('resolveAnchor:unidad-directa', {
    unitId: String(anchorId),
    catalogID: anchor.catalogID,
  })
  return { unitId: anchorId, unit: anchor }
}
